/* Macodesign — widget de tallas · build 2026-09-18
 * ARCHIVO GENERADO. No editar a mano: se arma desde sizing.js + widget-src.js.
 * El motor es el mismo que usa MarIA, así que las dos caras nunca se desincronizan.
 */
/* ──────────────────────────────────────────────────────────────────────────
 * Recomendador de tallas de MarIA — Macodesign
 * Vive en la ficha de producto. No llama a ninguna API: el cálculo ocurre en el
 * navegador de la clienta, así que es instantáneo y no cuesta nada por consulta.
 *
 * El motor (sizing.js) se inyecta arriba de este archivo en el build.
 * Fuente de verdad única: si cambia sizing.js, cambian el widget y MarIA.
 * ────────────────────────────────────────────────────────────────────────── */
(function () {
  'use strict';

// ═══ motor (sizing.js) ═══
/**
 * Motor de recomendación de tallas — Macodesign / MarIA
 * Calibrado el 13.08.2026 contra 2.888 unidades vendidas (365 días) y 117 tablas de medidas reales.
 *
 * Dos vías de entrada:
 *   A) Número de pantalón (sólo bottoms) — una pregunta, más preciso. Es la vía preferida.
 *   B) Altura + peso + calce — funciona en todo el catálogo.
 *
 *   import { recomendarTalla } from './sizing.js'
 *   recomendarTalla({ numeroPantalon: 38, handle: 'pantalon-lucena-mujer-rombos' }, DATASET)
 *   recomendarTalla({ altura: 165, peso: 62, calce: 'regular', handle: '...' }, DATASET)
 */

// ── Equivalencia de numeración europea → talla Maco ──────────────────────────
// Confirmado con la guía de tallas de la tienda (13.08.2026).
// Si algún día cambia el escalado, se cambia acá y nada más.
const EQUIV_EU = { 34: 'XS', 36: 'S', 38: 'M', 40: 'L', 42: 'XL' };
const EU_MIN = 34, EU_MAX = 42;
// Sobre 42 la numeración es ambigua: puede ser EU o la escala chilena de tienda
// por departamento. MarIA pregunta en vez de adivinar.
const EU_AMBIGUO_DESDE = 43;

// ── Constantes calibradas ────────────────────────────────────────────────────
// Recalibrado el 13.08.2026 contra 75 anclas reales: cada ficha declara "modelo mide 1,70
// y usa talla SM", así que sabemos qué talla le queda a un cuerpo conocido. El busto bajó
// de 4,0 a 1,0 porque las tablas de la marca son medidas DE CUERPO, no de prenda: sumarles
// holgura subía una talla de más. Con esto las anclas pasan de 62/75 a 64/75 aciertos y la
// mezcla predicha se acerca un poco más a las ventas reales.
const EASE_BASE  = { busto: 1.0, cintura: 1.0, cadera: 5.0 };
const EASE_CALCE = { ajustado: -2.0, regular: 0.0, holgado: 4.0 };
const CLAVES     = { bottom: ['cintura', 'cadera'], top: ['busto'], full: ['cintura', 'cadera'] };
const CLAVES_ALT = { top: ['cintura'] };   // tops cortos amarrados a la cintura
const ORDEN      = ['XS', 'S', 'SM', 'M', 'ML', 'L', 'XL', 'XXL'];
const idx = t => { const i = ORDEN.indexOf(t); return i < 0 ? 99 : i; };

// SM cubre XS-S-M, ML cubre M-L-XL. Para mapear un número a una escala SM/ML.
const A_ESCALA_DOBLE = { XS: 'SM', S: 'SM', M: 'SM', L: 'ML', XL: 'ML' };
// Hasta qué cuerpo llega cada talla doble, para poder compararlas con la tabla de la marca.
const TOPE_ESCALA = { SM: 'M', ML: 'XL' };

/**
 * Tabla de CUERPO de la marca, sacada de la mediana de las 46 guías que están escritas en
 * medidas de cuerpo. La cintura sale en pasos exactos de 4 cm (68·72·76·80·84), que es la
 * señal de que es un escalado real y no ruido.
 *
 * Existe porque el catálogo mezcla dos cosas distintas bajo el mismo formato:
 *   · 46 guías dicen "si tu busto mide 93, eres M"        → medidas de cuerpo
 *   · 21 guías dicen "esta prenda mide 112 de contorno"   → medidas de prenda
 * Tratarlas igual rompe por los dos lados: en las de cuerpo sube una talla de más, y en las
 * de prenda no sube nunca, porque un chaleco de 112 cm le cierra a casi cualquiera.
 */
const CUERPO_MARCA = {
  XS: { busto: 78,  cintura: 68, cadera: 94 },
  S:  { busto: 85,  cintura: 72, cadera: 98 },
  M:  { busto: 93,  cintura: 76, cadera: 102 },
  L:  { busto: 100, cintura: 80, cadera: 106 },
  XL: { busto: 107, cintura: 84, cadera: 110 },
};
const refMarca = (talla, m) => {
  const t = CUERPO_MARCA[talla] ? talla : TOPE_ESCALA[talla];
  return t && CUERPO_MARCA[t] ? CUERPO_MARCA[t][m] : null;
};

/** ¿Esta guía está escrita en medidas de prenda en vez de medidas de cuerpo? */
function esTablaDePrenda(tabla) {
  const ds = [];
  for (const f of tabla) for (const m of ['busto', 'cintura', 'cadera']) {
    const r = refMarca(f.talla, m);
    if (f[m] != null && r != null) ds.push(f[m] - r);
  }
  if (!ds.length) return false;
  ds.sort((a, b) => a - b);
  // 6 cm es el corte: por debajo cabe dentro de la variación normal entre guías,
  // por encima ya es holgura de diseño y no el cuerpo de la clienta.
  return ds[Math.floor(ds.length / 2)] > 6;
}

/**
 * Traduce la tabla a "qué cuerpo le queda bien a cada talla". En las guías de cuerpo ya
 * viene así; en las de prenda se reemplaza por el escalado de la marca, porque el número
 * de la prenda incluye una holgura de diseño que no dice nada sobre a quién le queda.
 * Las medidas originales no se tocan: se siguen mostrando en la tabla y se usan para
 * comprobar que la prenda físicamente le dé la vuelta al cuerpo.
 */
function capacidadDe(tabla) {
  if (!esTablaDePrenda(tabla)) return tabla;
  return tabla.map(f => {
    const c = Object.assign({}, f);
    for (const m of ['busto', 'cintura', 'cadera']) {
      const r = refMarca(f.talla, m);
      if (f[m] != null && r != null) c[m] = r;
    }
    return c;
  });
}

/**
 * Límites, en un solo lugar y configurables — no repartidos por el código.
 *   tecnico:  fuera de esto es un error de tipeo, no se deja avanzar.
 *   atipico:  puede ser real, pero se confirma antes de calcular.
 * Sirven para detectar errores de digitación. NO deciden si una talla queda bien:
 * eso lo deciden siempre las medidas reales de la prenda.
 */
const LIMITES = {
  // técnico: fuera de este rango (inclusive) no se avanza.
  tecnico: { altura: [100, 230], peso: [20, 200] },
  // atípico: se confirma bajo el mínimo o desde el máximo — 100 kg y 195 cm ya confirman.
  atipico: { altura: [145, 195], peso: [40, 100] },
  // Qué medida se le pide a la clienta cuando el cálculo queda en duda.
  medidaQueDecide: { bottom: 'cintura', top: 'busto', full: 'cintura' },
  // Rango en que una medida declarada por ella misma es creíble.
  medidaValida: { busto: [60, 160], cintura: [45, 160], cadera: [60, 180] },
};

function estimarCuerpo(alturaCm, pesoKg) {
  const bmi = pesoKg / Math.pow(alturaCm / 100, 2);
  const dh = alturaCm - 165;
  return {
    bmi,
    busto:   1.90 * bmi + 46 + 0.12 * dh,
    cintura: 2.20 * bmi + 21 + 0.10 * dh,
    cadera:  1.80 * bmi + 55 + 0.15 * dh,
  };
}

/** Tallas que este producto realmente ofrece, ordenadas. */
function tablaDe(prod) {
  let t = prod.medidas.slice().sort((a, b) => idx(a.talla) - idx(b.talla));
  if (prod.tallas?.length) {
    const f = t.filter(r => prod.tallas.includes(r.talla));
    if (f.length) t = f;
  }
  return t;
}

/** Equivalencia propia del producto si la guía la trae; si no, la de la marca. */
function equivalenciaDe(prod) {
  const propia = {};
  for (const fila of prod.medidas) {
    const o = fila.otros || {};
    for (const [k, v] of Object.entries(o)) {
      if (/equival|^talla$|talla \(/i.test(k)) {
        const nums = String(v).match(/\d+/g);
        // si un número aparece en dos rangos (ej. "34-36" y "36-38"), gana la talla menor
        if (nums) for (const n of nums) if (propia[+n] == null) propia[+n] = fila.talla;
      }
    }
  }
  return Object.keys(propia).length ? { mapa: propia, origen: 'producto' }
                                    : { mapa: EQUIV_EU, origen: 'marca' };
}

// ── Vía A': por la talla que usa normalmente (letras), para lo que no es pantalón ──
function porTallaHabitual(letra, prod) {
  const tabla = tablaDe(prod);
  const pos = tabla.map(r => r.talla);
  let t = String(letra).trim().toUpperCase();
  if (!ORDEN.includes(t)) return { ok: false, motivo: 'talla_desconocida' };

  if (!pos.includes(t)) {
    const doble = A_ESCALA_DOBLE[t];
    if (doble && pos.includes(doble)) t = doble;
    else {
      const arriba = pos.find(x => idx(x) >= idx(t));
      t = arriba || pos[pos.length - 1];
    }
  }
  return { ok: true, talla: t, via: 'habitual', tallaHabitual: letra,
           alternativa: null, confianza: 'alta', producto: prod, tabla };
}

// ── Vía A: por número de pantalón ────────────────────────────────────────────
function porNumero(numero, prod) {
  const tabla = tablaDe(prod);
  const pos = tabla.map(r => r.talla);
  const { mapa, origen } = equivalenciaDe(prod);

  if (numero >= EU_AMBIGUO_DESDE) {
    return { ok: false, motivo: 'numero_ambiguo', numero,
             detalle: 'sobre 42 no se sabe si es numeración europea o chilena' };
  }
  if (numero < EU_MIN) {
    return { ok: false, motivo: 'numero_fuera_de_rango', numero, extremo: 'bajo' };
  }

  // número exacto, o el inmediatamente superior (siempre se redondea hacia arriba)
  const claves = Object.keys(mapa).map(Number).sort((a, b) => a - b);
  const sobrepasa = numero > claves[claves.length - 1];
  const clave = claves.find(n => n >= numero) ?? claves[claves.length - 1];
  let talla = mapa[clave];

  // el producto puede no tener esa talla: escala doble SM/ML, o rango más corto
  if (!pos.includes(talla)) {
    const doble = A_ESCALA_DOBLE[talla];
    if (doble && pos.includes(doble)) talla = doble;
    else {
      const objetivo = idx(talla);
      const arriba = pos.find(t => idx(t) >= objetivo);
      if (arriba) talla = arriba;
      else return { ok: true, talla: pos[pos.length - 1], confianza: 'baja',
                    ajuste: 'justa', via: 'numero', producto: prod, tabla, numero,
                    alternativa: null, origenEquivalencia: origen,
                    nota: 'esta pieza no llega a su talla habitual' };
    }
  }

  const i = pos.indexOf(talla);
  const exacto = clave === numero;
  if (sobrepasa) {
    // pide un número mayor al que llega la marca: es la talla tope y va justa
    return { ok: true, talla, via: 'numero', origenEquivalencia: origen,
             alternativa: null, confianza: 'baja', ajuste: 'justa',
             nota: `la ${talla} es la talla mayor de esta pieza y su número (${numero}) la supera`,
             producto: prod, tabla, numero };
  }
  return {
    ok: true, talla, via: 'numero', origenEquivalencia: origen,
    alternativa: exacto ? null : (pos[i - 1] ?? null),
    confianza: exacto ? 'alta' : 'media',
    nota: exacto ? null : `su número (${numero}) cae entre dos tallas`,
    producto: prod, tabla, numero,
  };
}

// ── Vía B: por altura + peso + calce ─────────────────────────────────────────
function porCuerpo(altura, peso, calce, prod, medidas) {
  const cuerpo = estimarCuerpo(altura, peso);
  // Si la clienta se midió, su medida real reemplaza a la estimada. Una cinta métrica
  // le gana a cualquier fórmula: es el único dato que no tiene error de estimación.
  const medidas_reales = [];
  if (medidas) for (const m of ['busto', 'cintura', 'cadera']) {
    const v = Number(medidas[m]);
    const [lo, hi] = LIMITES.medidaValida[m];
    if (v >= lo && v <= hi) { cuerpo[m] = v; medidas_reales.push(m); }
  }
  const k = EASE_CALCE[calce] ?? 0;
  const req = {};
  for (const m of Object.keys(EASE_BASE)) req[m] = cuerpo[m] + (['gid://shopify/Page/146095636557','gid://shopify/Page/146095669325','gid://shopify/Page/146095702093','gid://shopify/Page/146095734861'].includes(prod.guia_page) ? 0 : EASE_BASE[m]) + k;

  // `cruda` son las medidas tal como están en la guía — las que se le muestran a la clienta.
  // `tabla` es esa misma guía traducida a medidas de cuerpo, que es con lo que se decide.
  const cruda = tablaDe(prod);
  const tabla = capacidadDe(cruda);
  // Una medida sólo sirve para elegir talla si está cargada en TODAS las filas. Si falta
  // en una, esa talla queda invisible para esa medida y el motor la salta por encima: en
  // un enterito con la M sin cadera se pasaba de S a L directo, dos tallas de golpe.
  // Si ninguna medida está completa se vuelve al criterio antiguo: peor una tabla con
  // huecos que no recomendar nada.
  const completa = m => tabla.every(r => r[m] != null);
  const algunaFila = m => tabla.some(r => r[m] != null);
  let claves = CLAVES[prod.familia].filter(completa);
  if (!claves.length) claves = CLAVES[prod.familia].filter(algunaFila);
  if (!claves.length && CLAVES_ALT[prod.familia]) {
    claves = CLAVES_ALT[prod.familia].filter(completa);
    if (!claves.length) claves = CLAVES_ALT[prod.familia].filter(algunaFila);
  }
  if (!tabla.length || !claves.length) {
    return { ok: false, motivo: 'producto_sin_medidas', producto: prod };
  }

  if (tabla.length === 1 && tabla[0].talla === 'TU') {
    const holgada = claves.every(m => (tabla[0][m] ?? 1e9) >= req[m]);
    return { ok: true, talla: 'TU', tallaUnica: true, via: 'cuerpo',
             confianza: holgada ? 'alta' : 'baja', producto: prod, cuerpo, requerido: req, tabla };
  }

  const porMedida = {}, margen = {};
  for (const m of claves) {
    const cand = tabla.find(r => r[m] != null && r[m] >= req[m]);
    if (cand) { porMedida[m] = cand.talla; margen[m] = cand[m] - req[m]; }
    else {
      const mx = tabla.filter(r => r[m] != null).reduce((a, b) => (b[m] > a[m] ? b : a));
      porMedida[m] = mx.talla; margen[m] = mx[m] - req[m];
    }
  }
  let talla = Object.values(porMedida).reduce((a, b) => (idx(b) > idx(a) ? b : a));

  // Piso físico. Las claves de arriba son las que deciden el calce, pero una prenda
  // tiene que dar la vuelta al cuerpo en TODAS sus medidas, sin holgura. Esto casi
  // nunca se activa; existe para cuerpos que se salen del molde de la marca — por
  // ejemplo alguien muy alta, donde cintura y cadera calzan pero el busto no.
  // El piso físico se comprueba contra las medidas REALES de la prenda, no contra la
  // traducción: lo que tiene que dar la vuelta al cuerpo es la tela, no el escalado.
  const TODAS = ['busto', 'cintura', 'cadera'];
  const pisoFalla = t => TODAS.some(m => {
    const f = cruda.find(r => r.talla === t);
    return f && f[m] != null && f[m] < cuerpo[m];
  });
  let subioPorPiso = false;
  if (pisoFalla(talla)) {
    const arriba = tabla.find(r => idx(r.talla) > idx(talla) && !pisoFalla(r.talla));
    if (arriba) { talla = arriba.talla; subioPorPiso = true; }
    else { talla = tabla[tabla.length - 1].talla; subioPorPiso = true; }
  }

  const manda = claves.filter(m => porMedida[m] === talla);
  const pos = tabla.map(r => r.talla);
  const i = pos.indexOf(talla);
  let margenMin = manda.length ? Math.min(...manda.map(m => margen[m]))
                               : Math.min(...claves.map(m => {
                                   const f = tabla.find(r => r.talla === talla);
                                   return f && f[m] != null ? f[m] - req[m] : 99;
                                 }));
  if (subioPorPiso) margenMin = Math.min(margenMin, ...TODAS.map(m => {
    const f = tabla.find(r => r.talla === talla);
    return f && f[m] != null ? f[m] - cuerpo[m] : 99;
  }));

  let confianza = 'alta', alternativa = null, nota = null, ajuste = null;
  if (margenMin < 0) {
    // Cuánto le falta a la prenda. Hasta ~3 cm es una prenda que entra justa y la
    // clienta puede decidir; más allá de eso ya no le sirve y decirlo es un favor.
    const deficit = -margenMin;
    confianza = 'baja';
    ajuste = deficit <= 3 ? 'justa' : 'fuera_de_rango';
    nota = deficit <= 3 ? 'le va a quedar justa' : 'esta pieza no llega a su talla';
    var _deficit = deficit;
  } else if (margenMin <= 2) {
    confianza = 'media'; alternativa = pos[i + 1] ?? null;
    nota = 'queda al límite: si prefiere comodidad, la siguiente talla';
  } else if (margenMin >= 8 && i > 0) {
    // sólo ofrecer bajar si la talla menor sigue sirviendo en TODAS las medidas,
    // no sólo en la que mandó: puede sobrar de cadera y no cerrar de cintura
    const menor = tabla[i - 1];
    if (claves.every(m => menor[m] == null || menor[m] >= req[m])) {
      confianza = 'media'; alternativa = menor.talla;
      nota = 'le queda holgada: si prefiere más ajustado, la talla menor';
    }
  }
  // En la talla mayor con poco margen: avisar que va justa. NUNCA ofrecer bajar —
  // si apenas entra en la mayor, la menor no le entra. (Bug corregido 13.08.2026:
  // la baja tasa de XL en ventas es autoselección de quien no compra, no una señal
  // de que convenga recomendar una talla menos.)
  if (margenMin >= 0 && i === pos.length - 1 && margenMin < 3) {
    confianza = 'media'; alternativa = null;
    nota = 'es la talla mayor de esta pieza y le va a quedar justa';
  }
  // El largo de las prendas está cortado sobre una modelo de 1,70. Fuera de un rango
  // razonable el largo deja de caer donde la foto muestra, y eso hay que decirlo.
  const largo = altura >= 178 ? 'alta' : (altura <= 155 ? 'baja' : null);

  return { ok: true, talla, alternativa, confianza, nota, ajuste, largo, subioPorPiso, via: 'cuerpo',
           deficit: typeof _deficit === 'number' ? Math.round(_deficit * 10) / 10 : null,
           manda, margen, porMedida, producto: prod, cuerpo, requerido: req,
           tabla: cruda, capacidad: tabla, medidas_reales };
}

// ── Stock ────────────────────────────────────────────────────────────────────
/**
 * Lee del propio Shopify qué tallas están disponibles. Se llama desde el navegador.
 * El endpoint /products/<handle>.js existe en todos los temas y siempre viene fresco.
 * Devuelve ['S','M'] o null si no se pudo consultar (ahí el motor sigue como si nada).
 */
async function stockDeLaPagina(handle) {
  try {
    const r = await fetch('/products/' + handle + '.js');
    if (!r.ok) return null;
    const p = await r.json();
    return p.variants.filter(v => v.available).map(v => String(v.title).trim().toUpperCase());
  } catch { return null; }
}

const CANON_TU = t => (['TALLA ÚNICA','TALLA UNICA','ÚNICA','UNICA','U','TU'].includes(t) ? 'TU' : t);

/**
 * Aplica el stock a una recomendación ya calculada.
 * La talla ideal NO se cambia — se dice la verdad — pero se marca si está agotada
 * y se propone la disponible más cercana, prefiriendo subir antes que bajar.
 */
function aplicarStock(r, stock, calce) {
  if (!r.ok || !r.talla) return r;
  r.calce = calce;
  if (!Array.isArray(stock)) return r;
  const disp = stock.map(t => CANON_TU(String(t).trim().toUpperCase()));
  r.stock_conocido = true;
  r.hay_stock = disp.includes(r.talla);
  if (r.hay_stock) {
    // si la segunda opción está agotada, no la ofrezcas
    if (r.alternativa && !disp.includes(r.alternativa)) r.alternativa = null;
    return r;
  }
  // Su talla NO se reemplaza. La recomendación sigue siendo la que es; lo único que
  // cambia es que hay que decirle que está agotada. Ofrecer una L porque la S no está
  // es venderle algo que no le va a quedar, y eso vuelve como cambio.
  r.talla_ideal = r.talla;
  r.alternativa = null;
  r.sugerencia_stock = alternativaContigua(r, disp);
  return r;
}

/**
 * Única alternativa admisible cuando su talla está agotada, y con las cuatro condiciones
 * puestas a la vez: contigua, con stock, sostenida por las medidas reales de la prenda,
 * y coherente con cómo dijo que le gusta que le quede. Si falta una, no se ofrece nada.
 */
function alternativaContigua(r, disp) {
  const tabla = r.capacidad || r.tabla;
  const pos = tabla.map(f => f.talla);
  const i = pos.indexOf(r.talla);
  if (i < 0 || !r.requerido) return null;

  for (const dir of ['mayor', 'menor']) {
    const j = dir === 'mayor' ? i + 1 : i - 1;
    if (j < 0 || j >= pos.length || !disp.includes(pos[j])) continue;
    const fila = tabla[j];
    const claves = Object.keys(r.requerido).filter(m => fila[m] != null);
    if (!claves.length) continue;

    if (dir === 'mayor') {
      // sirve si no le queda ridículamente grande. El umbral es el mismo que el motor
      // ya usa para llamar "holgada" a una talla, así que no hay dos criterios distintos.
      if (!claves.every(m => fila[m] - r.requerido[m] <= 8)) continue;
      // a quien la quiere al cuerpo no se le ofrece una talla más
      if (r.calce === 'ajustado') continue;
    } else {
      // sirve sólo si cierra en TODAS las medidas, no sólo en la que mandó
      if (!claves.every(m => fila[m] >= r.requerido[m])) continue;
      if (r.calce === 'holgado') continue;
    }
    return { talla: pos[j], direccion: dir, salto: 1,
             nota: dir === 'mayor' ? 'le quedaría un poco más suelta' : 'le quedaría un poco más al cuerpo' };
  }
  return null;
}

/**
 * Red de seguridad. Pase lo que pase adentro, esta función garantiza dos cosas:
 * que la talla recomendada exista en el producto, y que la alternativa no sea la misma.
 * Si la tabla de medidas y las variantes no calzan, es mejor no recomendar nada.
 */
function verificar(r, prod) {
  if (!r || !r.ok || !r.talla) return r;
  const vende = prod.tallas && prod.tallas.length ? prod.tallas : null;
  if (vende && !vende.includes(r.talla)) {
    return { ok: false, motivo: 'tallas_no_calzan', producto: prod };
  }
  if (r.alternativa && (r.alternativa === r.talla || (vende && !vende.includes(r.alternativa)))) {
    r.alternativa = null;
  }
  if (r.declarada === r.talla) r.declarada = null;
  if (r.sugerencia_stock && r.sugerencia_stock.talla === r.talla) r.sugerencia_stock = null;
  if (prod.fuente_ajuste && prod.fuente_ajuste.includes('provisional')) {
    if (r.confianza === 'alta') r.confianza = 'media';
    r.nota = (r.nota ? r.nota + '. ' : '') + 'Compara las medidas de la guía con una prenda similar';
  }
  return r;
}

// ── Entrada única ────────────────────────────────────────────────────────────
function recomendarTalla({ numeroPantalon, tallaHabitual, altura, peso, calce = 'regular', handle, stock, medidas }, dataset) {
  const prod = dataset.productos.find(p => p.handle === handle);
  if (!prod) return { ok: false, motivo: 'producto_sin_guia' };

  let res = null;
  // Vía A': la clienta declara la talla que usa normalmente (prendas que no son pantalón)
  if (tallaHabitual && !(numeroPantalon != null && prod.familia === 'bottom')) {
    res = porTallaHabitual(tallaHabitual, prod);
    if (res.ok) {
      if (altura && peso) res = combinar(res, porCuerpo(altura, peso, calce, prod, medidas), prod);
      return verificar(aplicarStock(aplicarCalceRelativo(res), stock, calce), prod);
    }
    res = null;
  }

  // Vía A: sólo tiene sentido en pantalones/jeans/faldas/shorts
  if (numeroPantalon != null && prod.familia === 'bottom') {
    res = porNumero(Number(numeroPantalon), prod);
    if (res.ok) {
      // si además dio altura y peso, se contrasta
      if (altura && peso) res = combinar(res, porCuerpo(altura, peso, calce, prod, medidas), prod);
      return verificar(aplicarStock(aplicarCalceRelativo(res), stock, calce), prod);
    }
    // número ambiguo o fuera de rango → se cae a la vía B si hay datos
    if (!(altura && peso)) return res;
  }

  const [aLo, aHi] = LIMITES.tecnico.altura, [pLo, pHi] = LIMITES.tecnico.peso;
  if (!(altura >= aLo && altura <= aHi) || !(peso >= pLo && peso <= pHi)) {
    return res ?? { ok: false, motivo: 'datos_insuficientes' };
  }
  return verificar(aplicarStock(aplicarCalceRelativo(porCuerpo(altura, peso, calce, prod, medidas)), stock, calce), prod);
}

/**
 * Junta lo que declaró la clienta con lo que dicen sus medidas.
 *
 * Antes el cuerpo ganaba siempre, y eso producía el error caro: alguien que sabe que
 * es SM recibía una ML porque la estimación de busto a partir de altura y peso se
 * desvía unos centímetros. Su talla habitual es información real, no un capricho.
 *
 *   Una talla de diferencia  → no se le lleva la contraria. Se deja la que ella declaró
 *                              y se marca `ambiguo` para poder preguntarle la medida
 *                              que decide, que es lo único que zanja la duda de verdad.
 *   Dos o más de diferencia  → algo no cuadra en los datos; mandan las medidas.
 *   Ella se midió            → manda la cinta métrica, sin discusión.
 */
// Centímetros de desacuerdo a partir de los cuales sí conviene preguntar una medida.
// Por debajo de esto la duda cabe dentro del error de la estimación.
const MARGEN_DUDA = 4;

/** Cuánto le sobra (o le falta, en negativo) a una talla frente a lo que ella necesita. */
function margenDe(talla, b) {
  if (!b.requerido || !b.tabla) return null;
  const fila = b.tabla.find(f => f.talla === talla);
  if (!fila) return null;
  const ms = Object.keys(b.requerido).filter(m => fila[m] != null);
  return ms.length ? Math.min(...ms.map(m => fila[m] - b.requerido[m])) : null;
}

function combinar(res, b, prod) {
  if (!b.ok) return res;
  res.largo = b.largo;
  res.medidas_reales = b.medidas_reales;
  // Se guarda lo que necesita su cuerpo aunque mande la talla declarada: es lo que
  // permite después juzgar si una talla contigua sirve o no cuando la suya está agotada.
  res.requerido = b.requerido; res.cuerpo = b.cuerpo; res.capacidad = b.capacidad;
  if (b.talla === res.talla) {
    res.ajuste = b.ajuste; res.nota = b.nota;
    res.alternativa = b.alternativa || res.alternativa || null;
    return res;
  }
  // El salto se mide en las tallas que ESTA prenda ofrece, no en la escala global:
  // en una pieza SM/ML esas dos son contiguas aunque en la escala general no lo parezcan.
  const pos = (b.tabla || res.tabla || []).map(f => f.talla);
  const ia = pos.indexOf(res.talla), ib = pos.indexOf(b.talla);
  const salto = (ia >= 0 && ib >= 0) ? Math.abs(ib - ia) : Math.abs(idx(b.talla) - idx(res.talla));
  const midio = (b.medidas_reales || []).length > 0;

  if (salto === 1 && !midio) {
    // Se queda con la que ella declaró. Preguntarle una medida sólo vale la pena cuando
    // el desacuerdo es grande: si su talla se queda corta por unos pocos centímetros,
    // eso cabe entero dentro del error de estimar el cuerpo con altura y peso, y no
    // justifica una pregunta más. Sólo se pregunta cuando la diferencia es de verdad.
    const falta = margenDe(res.talla, b);
    if (falta === null || falta >= -MARGEN_DUDA) {
      res.confianza = 'media';
      res.alternativa = b.talla;
      res.nota = 'su talla habitual y sus medidas quedan muy cerca una de otra';
      res.tabla = b.tabla;
      return res;
    }
    res.ambiguo = {
      declarada: res.talla, porCuerpo: b.talla,
      medida: LIMITES.medidaQueDecide[prod.familia] || 'busto',
    };
    res.alternativa = b.talla;
    res.confianza = 'media';
    res.ajuste = null;
    res.nota = 'la talla que usa y sus medidas no coinciden por una talla';
    res.tabla = b.tabla;
    return res;
  }

  // Desacuerdo de dos tallas o más. Antes se saltaba entero a la talla del cuerpo, y eso
  // producía un escalón al revés: como a una talla de distancia gana la declarada y a dos
  // ganaba el cuerpo, declarar S podía dar una talla MÁS GRANDE que declarar M. Ahora se
  // la mueve una sola talla en la dirección del cuerpo, que además respeta más lo que ella
  // dijo. Si se midió de verdad, el cuerpo sigue mandando entero: ahí no estamos
  // estimando, estamos leyendo una cinta métrica.
  const dir = (ia >= 0 && ib >= 0) ? Math.sign(ib - ia) : Math.sign(idx(b.talla) - idx(res.talla));
  let destino = b.talla;
  // Se aterriza en la talla PEGADA a la del cuerpo, del lado de la que ella declaró — no
  // a una talla de la declarada. Si dice XL y su cuerpo dice XS, lo correcto es dejarla en
  // S, no en L: el cuerpo sigue mandando, sólo se le concede una talla de margen.
  if (!midio && ia >= 0 && ib >= 0 && salto >= 2 && dir !== 0) {
    const j = ib - dir;
    if (j >= 0 && j < pos.length) destino = pos[j];
  }

  res.declarada = res.talla;
  res.discrepancia = { porCuerpo: b.talla, porDeclarada: res.talla, salto };
  res.talla = destino;
  res.tabla = b.tabla;
  res.medidas_reales = b.medidas_reales;

  if (destino === b.talla) {
    res.alternativa = null;
    res.confianza = midio ? b.confianza : (b.confianza === 'alta' ? 'media' : b.confianza);
    res.ajuste = b.ajuste; res.nota = b.nota; res.deficit = b.deficit;
    return res;
  }
  // La talla que se recomienda no es la que calculó el cuerpo, así que el ajuste y el
  // déficit —que se midieron contra esa otra— no le corresponden. Mejor no afirmar nada
  // que no se calculó para esta talla.
  res.alternativa = b.talla;
  res.confianza = 'media';
  res.ajuste = null;
  res.deficit = null;
  res.nota = 'su talla habitual y sus medidas apuntan a tallas distintas';
  return res;
}

function aplicarCalceRelativo(r) {
  if (!r.ok) return r;
  const d = r.producto.calce_relativo;
  if (d === 'corre_grande') r.nota = (r.nota ? r.nota + '. ' : '') + 'esta prenda es de calce amplio';
  if (d === 'corre_chico')  r.nota = (r.nota ? r.nota + '. ' : '') + 'esta prenda calza más al cuerpo que el resto de la marca';
  return r;
}

/** ¿Conviene preguntar el número en vez de altura y peso? */
function aceptaNumero(handle, dataset) {
  const p = dataset.productos.find(x => x.handle === handle);
  return !!p && p.familia === 'bottom' && p.tallas?.length > 1;
}

// ═══ fin del motor ═══


  const CFG = Object.assign({
    montarEn: '#maco-talla',
    politicaEn: '#maco-politica',
    datos: null,          // URL del tallas_maco.json
    dataset: null,        // o el JSON ya cargado
    stock: null,          // array o función; si no, se consulta a Shopify
    handle: null,
    whatsapp: '',
    abrirChat: null,      // function(ctx) que abre MarIA con el contexto precargado
    cambiosUrl: 'https://macodesign.reversso.cl/',
    politicasUrl: 'https://macodesign.cl/pages/cambios-devoluciones',
    onEvento: null,       // function(nombre, datos) para medir
    mostrarPolitica: true,
    recordar: true,
    reemplazarGuia: true, // ocupa el lugar del link "Guía de Tallas" y lo esconde
    reemplazarLinea: true,// ocupa el lugar de la línea de cambios que ya está en la ficha
    unificarEnvios: true, // deja "Envíos rápidos" con la misma forma que "Compra tranquila"
    textoBoton: '¿Cuál es mi talla?',
  }, window.MACO_TALLAS || {});

  let DATASET = null, PROD = null, DATOS = leerGuardado(), montado = false, btnActual = null;
  let ocultos = [], cajaAuto = null;
  let ov, pan, cuerpo, ultimo = null;

  // ── memoria de la visita ────────────────────────────────────────────────
  let memoria = {};
  function leerGuardado() {
    if (!CFG.recordar) return {};
    try { return JSON.parse(sessionStorage.getItem('maco_talla') || '{}'); } catch (e) { return memoria; }
  }
  function guardar(d) {
    DATOS = Object.assign({}, DATOS, d); memoria = DATOS;
    if (!CFG.recordar) return;
    try { sessionStorage.setItem('maco_talla', JSON.stringify(DATOS)); } catch (e) {}
  }

  function handleActual() {
    if (typeof CFG.handle === 'function') return CFG.handle();
    if (CFG.handle) return CFG.handle;
    const m = location.pathname.match(/\/products\/([^/?#]+)/);
    return m ? m[1] : (window.ShopifyAnalytics && window.ShopifyAnalytics.meta
      && window.ShopifyAnalytics.meta.product && window.ShopifyAnalytics.meta.product.handle) || null;
  }

  async function stockActual(handle) {
    if (typeof CFG.stock === 'function') return CFG.stock(handle);
    if (Array.isArray(CFG.stock)) return CFG.stock;
    try {
      const r = await fetch('/products/' + handle + '.js');
      if (!r.ok) return null;
      const p = await r.json();
      return p.variants.filter(v => v.available).map(v => String(v.title).trim().toUpperCase());
    } catch (e) { return null; }
  }

  // Única parte que depende del tema. Se sobrescribe con MACO_TALLAS.seleccionar
  /**
   * Deja marcado el pedido. Se inyectan dos propiedades ocultas en el formulario de compra,
   * así viajan con el "Agregar al carrito" que hace la clienta —no hace falta ninguna
   * llamada aparte— y quedan guardadas en el pedido para siempre.
   *
   * Van con guión bajo adelante: Shopify esconde esas propiedades del carro, del checkout
   * y del correo de confirmación. La clienta nunca las ve; sirven sólo para que después se
   * pueda cruzar qué pedidos pasaron por acá y cuáles de esos terminaron en cambio.
   */
  function marcarPedido(talla) {
    try {
      const form = document.querySelector('#MainContent form[action*="/cart/add"]')
                || document.querySelector('form[action*="/cart/add"]');
      if (!form) return;
      const poner = (nombre, valor) => {
        let i = form.querySelector('input[name="properties[' + nombre + ']"]');
        if (!i) {
          i = document.createElement('input');
          i.type = 'hidden';
          i.name = 'properties[' + nombre + ']';
          form.appendChild(i);
        }
        i.value = valor;
      };
      poner('_maco_talla', String(talla));
      poner('_maco_origen', 'recomendador');
    } catch (e) {}
  }

  function seleccionarEnFicha(talla) {
    marcarPedido(talla);
    // La ficha puede escribir la talla distinto a como la mostramos (hay variantes
    // en minúscula en Shopify). Se selecciona con la etiqueta original.
    const real = (PROD && PROD.etiquetas && PROD.etiquetas[talla]) || talla;
    if (typeof CFG.seleccionar === 'function') { CFG.seleccionar(real); return true; }
    const t = String(real).trim().toUpperCase();
    for (const i of document.querySelectorAll('input[type="radio"]'))
      if (String(i.value).trim().toUpperCase() === t) { i.click(); return true; }
    for (const s of document.querySelectorAll('select'))
      for (const o of s.options)
        if (String(o.value).trim().toUpperCase() === t || String(o.text).trim().toUpperCase() === t) {
          s.value = o.value; s.dispatchEvent(new Event('change', { bubbles: true })); return true;
        }
    for (const l of document.querySelectorAll('label,button,[role="radio"]'))
      if (l.textContent.trim().toUpperCase() === t) { l.click(); return true; }
    return false;
  }

  // ── diseño ───────────────────────────────────────────────────────────────
  // Flecha de cambio, dibujada en línea para no depender de ninguna fuente de íconos.
  const ICONO_CAMBIO =
    '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" '
    + 'stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">'
    + '<path d="M3 11a8 8 0 0 1 13.7-5.7L21 9"/><path d="M21 4v5h-5"/>'
    + '<path d="M21 13a8 8 0 0 1-13.7 5.7L3 15"/><path d="M3 20v-5h5"/></svg>';

  const ICONO_ENVIO =
    '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" '
    + 'stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">'
    + '<path d="M1 4h13v11H1z"/><path d="M14 8h4l3 3v4h-7z"/>'
    + '<circle cx="6" cy="18" r="2"/><circle cx="17" cy="18" r="2"/></svg>';

  // Cinta métrica, para la ruta que lleva a las medidas de la prenda.
  const ICONO_REGLA =
    '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" '
    + 'stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">'
    + '<rect x="2" y="7" width="20" height="10" rx="2.5"/>'
    + '<path d="M7 7v3.5M11 7v5M15 7v3.5M19 7v5"/></svg>';

  const ICONO_ATRAS =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" '
    + 'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 5l-7 7 7 7"/></svg>';

  const CSS = `
:root{
 /* Rosado y butter tomados del propio chatbot (botón Enviar y su encabezado),
    leídos de la tienda en vivo para que sean exactamente los mismos. */
 --mt-rosa:#E894BF;
 --mt-rosa-hover:#DF7FB1;
 --mt-rosa-header:#E894BF;
 --mt-butter:#FDFECB;
 /* CRO-021 (17-ago-2026): tokens PROPIOS del botón "¿Cuál es mi talla?",
    separados de --mt-rosa/--mt-rosa-hover a propósito. Esos dos siguen
    reutilizándose tal cual en el resto del modal (chips de talla, foco
    de inputs, botón de confirmar, tabla comparativa) — cambiarlos ahí
    habría repintado todo el widget de rosa del chatbot, y el pedido es
    solo corregir el color del botón disparador para que combine con la
    marca del sitio (macodesign.cl), no con el chatbot. Se reutilizan
    los tokens ya vigentes en el resto del sitio (--maco-rosa,
    --maco-rosa-oscuro, --maco-sale-text, --maco-cafe) en vez de
    hardcodear hex nuevos. */
 --mt-btn-bg:#FC8BC2;
 --mt-btn-bg-hover:#C873A3;
 --mt-btn-bg-active:#B85384;
 --mt-btn-text:#4D332F;
 /* Tinta: el único color de texto admitido sobre rosado. Da 6,7:1 de contraste;
    el gris que había antes daba 4,2:1 y por eso se leía apagado. */
 --mt-tinta:#33242F;
 --mt-texto:#33242F;
 --mt-texto-2:#6B6267;
 --mt-texto-3:#766D71;
 --mt-texto-4:#5F575B;
 --mt-crema:#FFFCF8;
 --mt-borde:#E8DDD7;
 --mt-off:#EAE4E6;
 --mt-off-texto:#8B8387;
 --mt-radio:23px;
 --mt-radio-chico:12px;
 --mt-sombra:0 12px 40px rgba(51,36,47,.16);
}
.mt-btn,.mt-p,.mt-p *,.mt-fila,.mt-fila *{font-family:inherit}
/* Si la fuente del chatbot está cargada en la página, el recomendador la usa: así los
   dos hablan con la misma letra. Si no está, cae a la del tema sin romperse. */
.mt-gi,.mt-gi *{font-family:'Glacial Indifference',inherit}
.mt-marca b,.mt-marca em{font-family:'Jost',inherit}
/* El widget no puede depender de que el tema declare box-sizing: si no lo hace, los
   campos con padding se desbordan del panel. Se declara acá y deja de importar. */
.mt-p,.mt-p *,.mt-btn,.mt-fila,.mt-fila *{box-sizing:border-box}
.mt-btn{display:inline-flex;align-items:center;justify-content:center;background:var(--mt-btn-bg);
 border:0;border-radius:17px;min-height:41px;padding:0 20px;font:inherit;font-size:14px;
 font-weight:500;color:var(--mt-btn-text);cursor:pointer;line-height:1.2;letter-spacing:.06em;
 transition:background .15s}
.mt-btn:hover{background:var(--mt-btn-bg-hover)}
.mt-btn:active{background:var(--mt-btn-bg-active)}
.mt-btn:focus-visible{outline:2px solid var(--mt-btn-bg-hover);outline-offset:3px}
.mt-btn:disabled,.mt-btn[disabled]{background:var(--mt-off);color:var(--mt-off-texto);cursor:not-allowed}
/* Se esconde sin sacarlo del flujo: si usáramos display:none la ficha daría un salto. */
.mt-oculto{visibility:hidden!important;pointer-events:none!important}

.mt-ov{position:fixed;inset:0;background:rgba(45,39,43,.28);z-index:2147483000;display:none}
.mt-ov.on{display:block}
.mt-p{position:fixed;z-index:2147483001;background:var(--mt-crema);width:440px;max-width:92vw;
 border-radius:var(--mt-radio);box-shadow:var(--mt-sombra);display:none;color:var(--mt-texto);
 left:50%;top:50%;transform:translate(-50%,-50%);overflow:hidden;max-height:92vh;
 display:none;flex-direction:column}
.mt-p.on{display:flex}
@media(max-width:560px){.mt-p{width:92vw;max-height:90vh}}

.mt-h{padding:19px 22px 20px;position:relative;flex:0 0 auto;z-index:2;
 background:var(--mt-rosa-header)}
/* Festón de semicírculos, copiado tal cual del encabezado del chatbot: círculos de 7 px
   en teselas de 14×8. La banda arranca 1 px dentro del encabezado, así que ni con un
   desajuste de subpíxel puede aparecer una costura entre los dos. */
.mt-h::after{content:"";position:absolute;left:0;right:0;bottom:-7px;height:8px;
 background:radial-gradient(circle at 7px 0, var(--mt-rosa-header) 7px, rgba(0,0,0,0) 7.5px);
 background-size:14px 8px;background-repeat:repeat-x}
.mt-marca{display:flex;align-items:center;gap:16px;color:var(--mt-butter)}
/* MACO al mismo tamaño óptico que en el chatbot: ~155 px de ancho en escritorio. */
.mt-marca b{font-weight:400;letter-spacing:.32em;font-size:30px;line-height:1;white-space:nowrap}
/* Separador vertical, igual que en el chatbot. */
.mt-marca i{flex:0 0 auto;width:1px;height:34px;background:var(--mt-butter);opacity:.5}
.mt-marca span{display:flex;flex-direction:column;gap:2px;min-width:0}
.mt-marca em{font-style:normal;font-size:16px;letter-spacing:.03em;font-weight:500;line-height:1.2}
.mt-h p{font-size:13px;color:var(--mt-tinta);margin:0;line-height:1.3;font-weight:400;
 letter-spacing:.04em;opacity:1}
@media(max-width:560px){.mt-marca{gap:13px}.mt-marca b{font-size:25px;letter-spacing:.3em}
 .mt-marca i{height:30px}.mt-marca em{font-size:15px}.mt-h p{font-size:13px}}
.mt-x{position:absolute;top:12px;right:13px;background:none;border:none;cursor:pointer;
 color:var(--mt-tinta);font-size:23px;line-height:1;padding:2px 6px}
.mt-x:hover{color:#000}

.mt-b{padding:20px 22px 22px;overflow-y:auto;flex:1 1 auto;background:var(--mt-crema);position:relative;z-index:1}
.mt-paso{font-size:13px;font-weight:400;color:var(--mt-texto-3);margin-bottom:11px;letter-spacing:.02em}
.mt-t{font-size:17.5px;font-weight:400;letter-spacing:.02em;margin:0 0 4px;line-height:1.35;color:var(--mt-tinta)}
.mt-pregunta{margin-bottom:15px}
.mt-q{font-size:14px;font-weight:400;letter-spacing:.02em;margin:0 0 14px;line-height:1.5;color:var(--mt-texto-2)}

/* Rejilla de columnas iguales: las tallas caben todas en una fila y ninguna queda más
   ancha que las otras. Antes era flex con wrap y la quinta se iba sola a la línea de
   abajo ocupando el ancho completo, que se leía como si fuera la opción destacada. */
.mt-ops{display:grid;grid-template-columns:repeat(var(--mt-cols,5),minmax(0,1fr));gap:6px}
.mt-op{min-width:0;min-height:43px;padding:0 4px;background:#fff;
 border:1px solid var(--mt-borde);border-radius:var(--mt-radio-chico);color:var(--mt-tinta);
 font-family:inherit;font-size:14px;font-weight:500;letter-spacing:.04em;cursor:pointer;
 text-align:center;transition:.15s}
.mt-op:hover,.mt-op:focus{background:#FFF9FC;border-color:var(--mt-rosa);outline:none}
/* Pantallas muy angostas: se aprieta el aire antes que la letra. */
@media(max-width:370px){.mt-ops{gap:4px}.mt-op{font-size:13px;letter-spacing:.03em}}
/* Un solo lenguaje para todo lo elegido: rosado con letra butter, igual que los botones.
   Ningún control interactivo lleva fondo amarillo — el butter queda para etiquetas. */
.mt-op.on{background:var(--mt-rosa);border-color:var(--mt-rosa);color:var(--mt-butter);font-weight:500}
.mt-op.on:hover{background:var(--mt-rosa-hover);border-color:var(--mt-rosa-hover)}
.mt-ops.ancho .mt-op{letter-spacing:.04em}
.mt-op:focus-visible{outline:2px solid var(--mt-tinta);outline-offset:2px}
.mt-op.on:before{content:'✓ ';font-size:12px}
/* El calce son tres opciones con texto largo: columnas iguales y algo más de aire. */
.mt-ops.ancho{grid-template-columns:repeat(3,minmax(0,1fr));gap:8px}

.mt-f{margin-bottom:13px}
.mt-f label{display:block;font-size:15px;font-weight:400;letter-spacing:.02em;color:var(--mt-tinta);margin-bottom:6px}
.mt-inp{position:relative;display:block}
.mt-inp input{width:100%;height:51px;padding:0 46px 0 15px;border:1px solid var(--mt-borde);
 border-radius:15px;background:#fff;font-family:inherit;font-size:15px;font-weight:400;
 color:var(--mt-texto);outline:none;-webkit-appearance:none}
.mt-inp input:focus{border-color:var(--mt-rosa)}
.mt-inp input.mal{border-color:#B3437E;background:#FFF6FA}
.mt-inp>span{position:absolute;right:15px;top:50%;transform:translateY(-50%);color:var(--mt-texto-3);
 font-size:14px;pointer-events:none}
.mt-err{font-size:12.5px;color:#B3437E;margin-top:5px;display:none}
.mt-f.mal .mt-err{display:block}

.mt-cta{display:flex;align-items:center;justify-content:center;width:100%;min-height:49px;
 padding:0 18px;background:var(--mt-rosa);color:var(--mt-butter);border:1px solid var(--mt-rosa);
 border-radius:17px;font-family:inherit;font-size:14.5px;font-weight:500;letter-spacing:.06em;
 line-height:1.2;cursor:pointer;margin-top:6px;transition:background .15s,border-color .15s}
@media(max-width:560px){.mt-cta{min-height:47px}}
.mt-cta:hover{background:var(--mt-rosa-hover);border-color:var(--mt-rosa-hover)}
.mt-cta:active{background:#D6739F;border-color:#D6739F}
.mt-cta:focus-visible{outline:2px solid var(--mt-rosa-hover);outline-offset:3px}
.mt-cta[disabled],.mt-cta:disabled{background:var(--mt-off);border-color:var(--mt-off);
 color:var(--mt-off-texto);cursor:not-allowed}
.mt-cta2{width:100%;min-height:46px;padding:0 18px;background:#fff;color:var(--mt-tinta);
 border:1px solid var(--mt-borde);border-radius:17px;font-family:inherit;font-size:14.5px;
 font-weight:500;letter-spacing:.06em;cursor:pointer;margin-top:9px;transition:.15s}
.mt-cta2:hover{border-color:var(--mt-rosa)}
.mt-min{display:block;width:100%;text-align:center;background:none;border:none;font-family:inherit;
 font-size:14px;font-weight:400;letter-spacing:.02em;color:var(--mt-texto-2);margin-top:13px;cursor:pointer}
.mt-min:hover{color:var(--mt-tinta)}
.mt-min:focus-visible{outline:2px solid var(--mt-tinta);outline-offset:2px;border-radius:6px}
.mt-nota{font-size:13px;font-weight:400;letter-spacing:.02em;color:var(--mt-texto-3);text-align:center;
 margin-top:11px;line-height:1.5}

.mt-r{text-align:center}
.mt-sello{display:inline-block;background:var(--mt-butter);border-radius:999px;padding:6px 15px;
 font-size:13px;font-weight:500;color:#51494D;margin-bottom:12px;letter-spacing:.03em}
.mt-big{font-size:53px;line-height:1;margin-bottom:14px;letter-spacing:.02em;font-weight:400}

.mt-big s{font-size:26px;color:var(--mt-off-texto);margin-right:12px;vertical-align:.32em;text-decoration-thickness:1px}
.mt-r p{font-size:15px;font-weight:400;letter-spacing:.01em;line-height:1.5;margin:0 0 16px;
 text-align:left;color:var(--mt-texto-4)}
.mt-r p strong{color:var(--mt-tinta);font-weight:500}

/* Aquí vivía el desplegable de medidas del resultado (.mt-des/.mt-tab/.mt-medir).
   Lo reemplazó la vista completa de medidas, que se define más abajo. */


.mt-pag{margin:14px 0}
.mt-r .mt-fila{margin-top:14px;text-align:left}
/* Módulo de beneficios de la ficha: un bloque, dos columnas, un solo par de líneas. */
.mt-ben{display:flex;align-items:stretch;gap:20px;padding:15px 0;
 border-top:1px solid var(--mt-borde);border-bottom:1px solid var(--mt-borde);color:var(--mt-tinta)}
.mt-ben-c{flex:1 1 0;min-width:0;display:flex;align-items:flex-start;gap:11px}
.mt-ben-sep{flex:0 0 1px;background:var(--mt-borde)}
.mt-ben-ic{flex:0 0 auto;width:18px;height:18px;margin-top:1px;display:flex;align-items:center;
 justify-content:center;color:var(--mt-texto-4)}
.mt-ben-ic svg{width:18px;height:18px}
.mt-ben-t{display:flex;flex-direction:column;gap:2px;min-width:0}
.mt-ben-t b{font-size:14px;font-weight:500;letter-spacing:.03em;color:var(--mt-tinta);line-height:1.3}
.mt-ben-t i{font-style:normal;font-size:12.5px;font-weight:400;letter-spacing:.02em;
 color:var(--mt-texto-2);line-height:1.4}
@media(max-width:700px){
 /* Mismo contenedor, dos filas: el divisor pasa de vertical a horizontal en vez de
    desaparecer, para que se siga leyendo como un solo módulo y no como dos tarjetas. */
 .mt-ben{flex-direction:column;gap:11px;padding:14px 0}
 .mt-ben-sep{flex:0 0 1px;height:1px;width:100%}
}

/* Dentro del panel: una sola línea, sin título grande. */
.mt-fila{border-top:1px solid var(--mt-borde);border-bottom:1px solid var(--mt-borde);
 font-family:inherit;color:var(--mt-tinta);background:transparent}
.mt-fila-h{display:flex;align-items:center;gap:10px;width:100%;background:none;border:0;
 padding:12px 2px;font:inherit;text-align:left;cursor:pointer;color:inherit}
.mt-fila-h:focus-visible{outline:2px solid var(--mt-tinta);outline-offset:-2px;border-radius:6px}
.mt-fila-ic{flex:0 0 auto;width:17px;height:17px;display:flex;align-items:center;
 justify-content:center;color:var(--mt-texto-4)}
.mt-fila-ic svg{width:17px;height:17px}
.mt-fila-tx{flex:1 1 auto;min-width:0;font-size:13px;font-weight:400;letter-spacing:.02em;
 color:var(--mt-texto-2);line-height:1.4}
.mt-fila-ar{flex:0 0 auto;font-size:12px;color:var(--mt-texto-4)}
.mt-fila-c{display:none;padding:0 2px 13px 27px;font-size:13px;line-height:1.55;color:var(--mt-texto-2)}
.mt-fila.abierto .mt-fila-c{display:block}
.mt-fila-c p{margin:0 0 8px}
.mt-fila-c b{color:var(--mt-tinta);font-weight:500}
@media(max-width:560px){.mt-fila-tx{font-size:12.5px}}
.mt-pol-link{display:inline-block;margin-top:9px;color:var(--mt-tinta);text-decoration:none;
 border-bottom:1px solid rgba(51,36,47,.35);font-size:14px}
@media(max-width:560px){.mt-fila-tx i{font-size:12px}}
.mt-agot{text-align:center;font-size:13px;letter-spacing:.06em;text-transform:uppercase;
 color:var(--mt-texto-3);margin:-6px 0 12px}
.mt-sep{margin-top:14px}
.mt-conf-datos{display:flex;gap:10px;margin:0 0 16px}
.mt-conf-datos span{flex:1 1 0;display:flex;flex-direction:column;gap:2px;background:#fff;
 border:1px solid var(--mt-borde);border-radius:var(--mt-radio-chico);padding:10px 13px;
 font-size:12.5px;letter-spacing:.02em;color:var(--mt-texto-2)}
.mt-conf-datos b{font-size:16px;font-weight:500;color:var(--mt-tinta)}

/* ── Vista "Medidas de esta prenda" ─────────────────────────────────────────
   Es la segunda ruta del asistente: se entra desde la primera pantalla sin
   responder ninguna pregunta. Vive dentro del mismo panel, con la misma
   identidad: crema, tinta, rosado, butter y pesos hasta 500. */

/* El panel se ensancha sólo mientras se ven las medidas: una tabla de cinco
   columnas no cabe con dignidad en 440 px. En móvil no cambia nada. */
.mt-p{transition:width .22s ease}
.mt-p.mt-medidas{width:min(580px,92vw)}
@media(max-width:559px){.mt-p.mt-medidas{width:92vw}}
@media(prefers-reduced-motion:reduce){.mt-p{transition:none}}

.mt-volver{display:inline-flex;align-items:center;gap:7px;background:none;border:0;font:inherit;
 font-size:13.5px;font-weight:400;letter-spacing:.02em;color:var(--mt-texto-2);cursor:pointer;
 padding:3px 7px 3px 0;margin:0 0 14px;border-radius:7px}
.mt-volver:hover{color:var(--mt-tinta)}
.mt-volver:focus-visible{outline:2px solid var(--mt-tinta);outline-offset:2px}
.mt-volver svg{width:15px;height:15px;flex:0 0 auto}

.mt-md-cab{margin-bottom:16px}
.mt-md-t1{font-size:17.5px;font-weight:400;letter-spacing:.02em;line-height:1.3;margin:0;color:var(--mt-tinta)}
.mt-md-t2{font-size:12px;font-weight:500;letter-spacing:.09em;text-transform:uppercase;
 color:var(--mt-texto-3);margin:6px 0 0}
.mt-md-nota{font-size:12.5px;line-height:1.5;color:var(--mt-texto-3);margin:9px 0 0}
.mt-md-bloque+.mt-md-bloque{margin-top:20px}
.mt-md-bt{font-size:11.5px;font-weight:500;letter-spacing:.08em;text-transform:uppercase;
 color:var(--mt-texto-3);margin:0 0 8px}

.mt-md-t{width:100%;border-collapse:separate;border-spacing:0;background:#fff;
 border:1px solid var(--mt-borde);border-radius:var(--mt-radio-chico);overflow:hidden}
/* El título de la tabla existe para quien la escucha, no para quien la mira. */
.mt-md-t caption{position:absolute;width:1px;height:1px;overflow:hidden;white-space:nowrap;
 clip:rect(0 0 0 0);clip-path:inset(50%)}
.mt-md-t th,.mt-md-t td{text-align:left;padding:10px 11px;vertical-align:top}
.mt-md-t thead th{background:#FFFEF1;font-size:11.5px;font-weight:500;letter-spacing:.03em;
 line-height:1.3;color:var(--mt-tinta);border-bottom:1px solid var(--mt-borde)}
.mt-md-t tbody th{font-size:14px;font-weight:500;letter-spacing:.05em;color:var(--mt-tinta);white-space:nowrap}
.mt-md-t tbody td{font-size:13.5px;font-weight:400;color:var(--mt-texto-4);white-space:nowrap}
.mt-md-t tbody tr+tr th,.mt-md-t tbody tr+tr td{border-top:1px solid var(--mt-borde)}
.mt-md-t tbody tr.on th,.mt-md-t tbody tr.on td{background:#FFF4F9}
.mt-md-vacio{color:var(--mt-off-texto)}
.mt-md-tag{display:inline-block;margin-left:8px;background:var(--mt-rosa);color:var(--mt-butter);
 border-radius:999px;padding:3px 9px;font-size:10.5px;font-weight:500;letter-spacing:.07em;
 text-transform:uppercase;vertical-align:1px}

/* Móvil: la misma tabla se lee como una tarjeta por talla. Es el mismo marcado —
   no hay dos versiones del contenido que puedan quedar desalineadas— y cada valor
   lleva su etiqueta al lado, así que no hace falta desplazarse de lado. */
@media(max-width:559px){
 .mt-md-t{display:block;border:0;border-radius:0;background:transparent;overflow:visible}
 .mt-md-t thead{position:absolute;width:1px;height:1px;overflow:hidden;white-space:nowrap;
  clip:rect(0 0 0 0);clip-path:inset(50%)}
 .mt-md-t tbody,.mt-md-t th,.mt-md-t td{display:block}
 .mt-md-t tbody tr{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:11px 12px;
  background:#fff;border:1px solid var(--mt-borde);border-radius:var(--mt-radio-chico);
  padding:13px;margin-bottom:9px}
 .mt-md-t tbody tr:last-child{margin-bottom:0}
 .mt-md-t tbody tr.on{border-color:var(--mt-rosa)}
 .mt-md-t tbody tr.on th,.mt-md-t tbody tr.on td{background:transparent}
 .mt-md-t tbody th{grid-column:1/-1;padding:0;font-size:15px}
 .mt-md-t tbody tr+tr th,.mt-md-t tbody tr+tr td{border-top:0}
 .mt-md-t tbody td{padding:0;font-size:15px;font-weight:500;color:var(--mt-tinta);
  min-width:0;white-space:normal;overflow-wrap:anywhere}
 .mt-md-t tbody td::before{content:attr(data-e);display:block;font-size:11.5px;font-weight:400;
  letter-spacing:.02em;line-height:1.3;color:var(--mt-texto-2);margin-bottom:3px}
 /* En la tarjeta, una celda sin dato no aporta nada: se omite en vez de dejar un guión. */
 .mt-md-t tbody td.sin{display:none}
}
@media(max-width:340px){
 .mt-md-t tbody tr{padding:11px;gap:10px}
 .mt-md-t tbody td{font-size:14px}
 .mt-md-tag{margin-left:6px;padding:2px 7px;font-size:10px}
}

.mt-md-medir{margin-top:18px;border-top:1px solid var(--mt-borde);padding-top:14px;
 font-size:13.5px;line-height:1.6;color:var(--mt-texto-2)}
.mt-md-medir b{display:block;color:var(--mt-tinta);font-weight:500;margin-bottom:5px;
 font-size:11.5px;letter-spacing:.08em;text-transform:uppercase}
.mt-md-sin{background:#fff;border:1px solid var(--mt-borde);border-radius:var(--mt-radio-chico);
 padding:16px;font-size:14px;line-height:1.55;color:var(--mt-texto-2)}
.mt-md-pie{margin-top:18px}
.mt-md-pie .mt-cta2{margin-top:0}

/* Acceso a la vista desde el resto de las pantallas. */
.mt-md-otra{margin-top:16px;padding-top:15px;border-top:1px solid var(--mt-borde)}
.mt-md-otra .mt-cta2{display:flex;align-items:center;justify-content:center;gap:9px;margin-top:0}
.mt-md-otra .mt-cta2 svg{flex:0 0 auto}
.mt-r .mt-md-otra{text-align:left}
`;

  // ── medición ────────────────────────────────────────────────────────────
  // Se emite por cuatro vías para que enganche con lo que ya tengas montado.
  function evento(nombre, datos) {
    const d = Object.assign({
      producto: PROD ? PROD.handle : null,
      titulo: PROD ? PROD.titulo : null,
    }, datos || {});
    try { if (typeof CFG.onEvento === 'function') CFG.onEvento(nombre, d); } catch (e) {}
    try { (window.dataLayer = window.dataLayer || []).push(Object.assign({ event: 'maco_talla_' + nombre }, d)); } catch (e) {}
    try { document.dispatchEvent(new CustomEvent('maco:talla', { detail: Object.assign({ evento: nombre }, d) })); } catch (e) {}
    try { if (typeof window.gtag === 'function') window.gtag('event', 'maco_talla_' + nombre, d); } catch (e) {}
  }

  /**
   * Cualquier cosa que se rompa acá adentro se reporta como un evento más. Sin esto un
   * error queda invisible: la clienta ve "no pudimos calcular tu talla", se va, y nadie
   * se entera nunca. El mensaje se recorta porque va a analítica, no a un log.
   */
  function fallo(donde, e) {
    try {
      evento('error', {
        donde: donde,
        mensaje: String((e && (e.message || e)) || '').slice(0, 140),
        paso: (typeof DATOS !== 'undefined' && DATOS) ? JSON.stringify({
          habitual: DATOS.habitual, numero: DATOS.numero,
          altura: DATOS.altura, peso: DATOS.peso, calce: DATOS.calce,
        }) : null,
      });
    } catch (x) {}
  }

  const esc = s => String(s).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

  // ── política, según la categoría del producto ───────────────────────────
  function politica() {
    return (PROD && PROD.politica) || (DATASET && DATASET.politica_defecto) || { cambio: 30, devolucion: 10, tipo: 'normal' };
  }
  function politicaTexto() {
    const p = politica();
    return p.tipo === 'fiesta'
      ? 'En prendas de la categoría Fiesta tienes <b>' + p.cambio + ' días corridos</b> para solicitar un cambio o devolución.'
      : 'Si no te queda como esperabas, tienes <b>' + p.cambio + ' días corridos</b> para solicitar un cambio y <b>'
        + p.devolucion + ' días corridos</b> para solicitar una devolución.';
  }
  // Dentro del panel se usa la misma fila que en la ficha. Antes era una tarjeta
  // amarilla y se comía la atención del resultado, que es lo único que importa ahí.
  function cajaConfianza() { return politicaHTML(); }
  function resumenPolitica() {
    const p = politica();
    return p.tipo === 'fiesta'
      ? p.cambio + ' días cambios o devoluciones'
      : p.cambio + ' días cambios · ' + p.devolucion + ' días devoluciones';
  }
  // En la ficha va como una fila, no como una caja: ahí abajo compite con "Agregar al
  // carrito" y el mensaje se lee mejor como un dato más de la compra que como un cartel.
  // El detalle completo queda a un clic, para quien quiera leerlo.
  /**
   * Un solo módulo con los dos beneficios, no dos bloques separados: en escritorio van en
   * dos columnas del mismo ancho y en móvil uno bajo el otro, pero siempre dentro del mismo
   * par de líneas. Ninguno pesa más que el otro.
   */
  function moduloBeneficios(tituloEnvio) {
    const col = (ic, t, r) => '<div class="mt-ben-c">'
      + '<span class="mt-ben-ic" aria-hidden="true">' + ic + '</span>'
      + '<span class="mt-ben-t"><b>' + t + '</b><i>' + r + '</i></span></div>';
    return '<div class="mt-ben">'
      + col(ICONO_CAMBIO, 'Compra tranquila', resumenPolitica())
      + (tituloEnvio ? '<div class="mt-ben-sep"></div>'
          + col(ICONO_ENVIO, esc(tituloEnvio), 'Revisa plazos según tu ubicación') : '')
      + '</div>';
  }

  // Dentro del panel el mensaje va en una sola línea: ahí lo que importa es la talla,
  // no un bloque informativo. El detalle queda a un clic.
  function politicaHTML() {
    const url = CFG.politicasUrl || CFG.cambiosUrl;
    return '<div class="mt-fila">'
      + '<button type="button" class="mt-fila-h" aria-expanded="false">'
      + '<span class="mt-fila-ic" aria-hidden="true">' + ICONO_CAMBIO + '</span>'
      + '<span class="mt-fila-tx">Compra tranquila · ' + resumenPolitica() + '</span>'
      + '<span class="mt-fila-ar" aria-hidden="true">↓</span></button>'
      + '<div class="mt-fila-c"><p>La prenda debe estar sin uso, en perfecto estado y con su '
      + 'etiqueta original. Gestiona tu solicitud en <b>macodesign.reversso.cl</b>.</p>'
      + (url ? '<a href="' + url + '" target="_blank" rel="noopener" class="mt-pol-link">Ver políticas completas</a>' : '')
      + '</div></div>';
  }

  function enganchaFila(raiz) {
    raiz.querySelectorAll('.mt-fila').forEach(f => {
      const h = f.querySelector('.mt-fila-h');
      if (!h || h.dataset.listo) return;
      h.dataset.listo = '1';
      h.onclick = () => {
        const ab = !f.classList.contains('abierto');
        f.classList.toggle('abierto', ab);
        h.setAttribute('aria-expanded', String(ab));
        f.querySelector('.mt-fila-ar').textContent = ab ? '↑' : '↓';
        if (ab && f.querySelector('.mt-pol-link')) evento('ver_politicas', { desde: 'ficha' });
      };
      const a = f.querySelector('.mt-pol-link');
      if (a) a.addEventListener('click', () => evento('ver_politicas', { desde: 'ficha_link' }));
    });
  }

  function montarPolitica() {
    if (!CFG.mostrarPolitica) return;
    const d = document.querySelector(CFG.politicaEn);
    if (d) { d.className += ' mt-pag'; conFuente(d); d.innerHTML = moduloBeneficios(null); return; }
    if (!CFG.reemplazarLinea) return;
    // La ficha trae la línea de cambios y la de envíos por separado, con tipografías y
    // alturas distintas. Se reemplazan las dos por un módulo único.
    const anclas = anclaLinea();
    if (!anclas.length) return;
    const env = CFG.unificarEnvios ? anclaEnvio() : null;
    cajaAuto = document.createElement('div');
    cajaAuto.className = 'mt-pag'; conFuente(cajaAuto);
    cajaAuto.innerHTML = moduloBeneficios(env ? env.titulo : null);
    anclas[0].parentNode.insertBefore(cajaAuto, anclas[0]);
    anclas.forEach(esconder);
    if (env) esconder(env.nodo);
    destapar(cajaAuto);
  }

  /** La línea de envíos del tema: se toma su texto y se esconde la original. */
  function anclaEnvio() {
    // Ojo con "envío gratis": eso vive en la barra de anuncios y no es esta fila.
    const re = /env[ií]os?\s+r[aá]pidos|despacho\s+(a|en)\s/i;
    const ref = document.querySelector('form[action*="/cart/add"]');
    const n = buscar(ref, 'p,div,span,small,li',
      (x, t) => t.length > 6 && t.length < 90 && re.test(t)
                && x.children.length <= 1 && !x.querySelector('input,select,button,a'));
    return n ? { titulo: (n.textContent || '').trim(), nodo: envoltorio(n) } : null;
  }

  // Algunos temas animan los bloques al hacer scroll y arrancan en visibility:hidden.
  // Lo que insertamos hereda ese estado y no se ve nunca, así que se fuerza visible.
  function destapar(n) { n.style.visibility = 'visible'; n.style.opacity = '1'; }

  // ── encontrar qué reemplazar en la ficha ────────────────────────────────
  function esconder(n) { ocultos.push([n, n.style.display]); n.style.display = 'none'; }
  function restaurar() { ocultos.forEach(p => { p[0].style.display = p[1]; }); ocultos = []; }
  // Ojo: offsetWidth no basta. Los temas suelen tener una copia del mismo texto dentro del
  // carrito lateral, que ocupa espacio pero está en visibility:hidden. Si anclamos ahí, la
  // caja existe y no se ve nunca. No se mira la opacidad a propósito: los bloques con
  // animación de entrada arrancan en opacidad 0 y son perfectamente válidos.
  function visible(n) {
    if (!(n.offsetWidth > 0 || n.offsetHeight > 0)) return false;
    if (n.checkVisibility) { try { return n.checkVisibility({ visibilityProperty: true }); } catch (e) {} }
    let x = n;
    for (let i = 0; i < 12 && x; i++) {
      if (getComputedStyle(x).visibility === 'hidden') return false;
      x = x.parentElement;
    }
    return true;
  }

  // Sólo se busca alrededor de la ficha: así no se toca el link de la guía que vive
  // en el menú o en el pie de página.
  function ambito() {
    const f = document.querySelector('form[action*="/cart/add"], variant-radios, variant-selects');
    if (!f) return null;
    let n = f;
    for (let i = 0; i < 6 && n.parentElement && n.parentElement !== document.body; i++) n = n.parentElement;
    return n;
  }
  // Muchas tiendas repiten el mismo texto (marquee, banners, acordeones). En vez de tomar
  // el primero que aparece, se elige el que está más cerca del formulario de compra.
  function cercania(ref, n) {
    let d = 0, a = ref;
    while (a) { if (a.contains(n)) return d; a = a.parentElement; d++; }
    return 999;
  }
  function buscar(ref, sel, prueba, todos) {
    const zonas = [ambito(), document.body].filter(Boolean);
    for (let z = 0; z < zonas.length; z++) {
      const nodos = zonas[z].querySelectorAll(sel);
      let mejor = null, mejorD = 999; const lista = [];
      for (let i = 0; i < nodos.length; i++) {
        const n = nodos[i];
        if (n.closest('header,footer,nav,dialog,cart-drawer,[class*="drawer"],[class*="modal"],'
              + '[class*="announcement"],[class*="anuncio"],[class*="marquee"],'
              + '[aria-hidden="true"],[role="banner"],[role="navigation"],[role="contentinfo"]')) continue;
        if (!visible(n)) continue;
        if (!prueba(n, (n.textContent || '').trim())) continue;
        const d = ref ? cercania(ref, n) : 0;
        lista.push(n);
        if (d < mejorD) { mejor = n; mejorD = d; }
      }
      if (mejor && todos) { lista.splice(lista.indexOf(mejor), 1); lista.unshift(mejor); }
      if (mejor) return todos ? lista : mejor;
    }
    return todos ? [] : null;
  }
  // Si el texto vive dentro de un bloque que sólo lo contiene a él (típico bloque con
  // ícono), se esconde el bloque entero para no dejar el ícono huérfano.
  function envoltorio(n) {
    const t = (n.textContent || '').trim();
    let x = n;
    for (let i = 0; i < 3; i++) {
      const p = x.parentElement;
      if (!p || p === document.body || (p.textContent || '').trim() !== t) break;
      x = p;
    }
    return x;
  }

  // El enlace original a la guía, guardado tal cual. Se usa para dos cosas: esconderlo,
  // y encontrar desde ahí la tabla de medidas que el tema ya trae en la ficha.
  let anclaGuiaNodo = null;
  function anclaGuia() {
    const re = /(gu[ií]a|tabla)\s+de\s+tallas|size\s*(chart|guide)/i;
    const ref = document.querySelector('variant-radios, variant-selects, [class*="variant"], form[action*="/cart/add"]');
    const n = buscar(ref, 'a,button,summary,[class*="size-chart"],[class*="sizechart"],[class*="guia"]',
      (x, t) => t.length > 0 && t.length < 40 && re.test(t));
    anclaGuiaNodo = n || null;
    return n && envoltorio(n);
  }

  // Devuelve todas las copias de la línea: algunos temas la repiten (versión escritorio y
  // móvil). Si queda una suelta se ve como si nos contradijéramos, así que se esconden todas.
  function anclaLinea() {
    const re = /(cambiar|cambio)\s+(o|y)\s+(devolver|devoluci)|no te queda bien/i;
    const ref = document.querySelector('form[action*="/cart/add"]');
    const ns = buscar(ref, 'p,div,span,small,li',
      (x, t) => t.length > 10 && t.length < 170 && re.test(t)
                && x.children.length <= 1 && !x.querySelector('input,select,button,a'), true);
    const envs = [];
    for (let i = 0; i < ns.length; i++) {
      const e = envoltorio(ns[i]);
      if (envs.indexOf(e) === -1 && !envs.some(v => v.contains(e))) envs.push(e);
    }
    return envs;
  }

  // El chatbot trae su propia fuente. Si está cargada, el recomendador la adopta; si no,
  // se queda con la del tema. Se comprueba en vez de asumirla para no caer en Times.
  const FUENTE_CHAT = 'Glacial Indifference';
  function tieneFuenteChat() {
    try { return !!(document.fonts && document.fonts.check('14px "' + FUENTE_CHAT + '"')); }
    catch (e) { return false; }
  }
  // El chatbot carga su fuente después que nosotros, así que no basta con mirarla una vez:
  // se guardan los nodos y se revisan de nuevo cuando el navegador termina de cargar fuentes.
  const nodosFuente = [];
  function conFuente(el) { nodosFuente.push(el); if (tieneFuenteChat()) el.classList.add('mt-gi'); }
  function revisarFuente() {
    if (!tieneFuenteChat()) return;
    for (let i = 0; i < nodosFuente.length; i++) nodosFuente[i].classList.add('mt-gi');
  }
  try { if (document.fonts && document.fonts.ready) document.fonts.ready.then(revisarFuente); } catch (e) {}
  setTimeout(revisarFuente, 2500);
  setTimeout(revisarFuente, 6000);

  /**
   * Un solo asistente abierto a la vez. El chatbot se minimiza con su propio botón de
   * cerrar —así conserva la conversación— y su burbuja flotante se esconde mientras el
   * recomendador está arriba. Al cerrar sólo vuelve la burbuja, nunca la ventana entera.
   */
  let burbujaChat = null;
  const esNuestro = n => !!(n && n.closest && (n.closest('.mt-p') || n.closest('.mt-ov')));

  /**
   * El chat expone ids estables (#maria-panel, #maria-btn, #maria-x). Se usan esos: buscar
   * por tamaño no sirve —en pantallas con zoom o alta densidad el panel mide el doble y
   * cualquier umbral en píxeles falla— y buscar por texto depende de la redacción.
   * El rastreo por posición fija queda sólo como red de seguridad si los ids cambiaran.
   */
  function panelChat() {
    const p = document.getElementById('maria-panel');
    if (p) return p;
    const marca = [...document.querySelectorAll('span,div,p,h1,h2,h3')].find(n =>
      !esNuestro(n) && n.children.length === 0
      && /Asistente de Maco|Escríbeme lo que necesites/i.test(n.textContent || ''));
    let n = marca;
    while (n && n !== document.body) {
      if (getComputedStyle(n).position === 'fixed') return n;
      n = n.parentElement;
    }
    return null;
  }
  function burbuja() {
    const b = document.getElementById('maria-btn');
    if (b) return b;
    const ns = document.querySelectorAll('button,div,a');
    for (let i = ns.length - 1; i >= 0; i--) {
      const n = ns[i];
      if (esNuestro(n)) continue;
      const cs = getComputedStyle(n), r = n.getBoundingClientRect();
      if (cs.position === 'fixed' && r.width > 30 && r.width < 140 && r.height > 30 && r.height < 140
          && Math.abs(r.width - r.height) < 18
          && r.bottom > innerHeight - 260 && r.right > innerWidth - 280) return n;
    }
    return null;
  }
  function ocultarChat() {
    try {
      const p = panelChat();
      if (p && p.classList.contains('open')) {
        // Se pulsa el propio botón de cerrar del chat: es la misma acción que haría la
        // clienta, así que la conversación queda guardada y la burbuja vuelve sola.
        const x = document.getElementById('maria-x')
               || [...p.querySelectorAll('button,[role="button"],a')].find(b =>
                    /^(cerrar|close|✕|×|x)$/i.test(
                      (b.getAttribute('aria-label') || b.title || b.textContent || '').trim()));
        if (x) x.click(); else p.classList.add('mt-oculto');
      }
      burbujaChat = burbuja();
      if (burbujaChat) burbujaChat.classList.add('mt-oculto');
    } catch (e) {}
  }
  function mostrarChat() {
    try {
      if (burbujaChat) burbujaChat.classList.remove('mt-oculto');
      const p = panelChat();
      if (p) p.classList.remove('mt-oculto');
    } catch (e) {}
    burbujaChat = null;
  }

  // ── scroll de la página mientras el modal está abierto ──────────────────
  let scrollGuardado = '';
  function bloquearScroll() {
    const barra = window.innerWidth - document.documentElement.clientWidth;
    scrollGuardado = document.body.style.cssText;
    document.body.style.overflow = 'hidden';
    if (barra > 0) document.body.style.paddingRight = barra + 'px';
  }
  function liberarScroll() { document.body.style.cssText = scrollGuardado; }

  // ── foco: entra al modal, no se escapa, y vuelve al botón al cerrar ─────
  const FOCO = 'button,[href],input,select,textarea,[tabindex]:not([tabindex="-1"])';
  function focoDentro(e) {
    if (e.key !== 'Tab' || !pan || !pan.classList.contains('on')) return;
    const f = [...pan.querySelectorAll(FOCO)].filter(x => x.offsetWidth || x.offsetHeight);
    if (!f.length) return;
    const primero = f[0], ultimo = f[f.length - 1];
    if (e.shiftKey && document.activeElement === primero) { e.preventDefault(); ultimo.focus(); }
    else if (!e.shiftKey && document.activeElement === ultimo) { e.preventDefault(); primero.focus(); }
  }
  function enfocarPrimero() {
    if (!cuerpo) return;
    const f = cuerpo.querySelector(FOCO);
    if (f) { try { f.focus({ preventScroll: true }); } catch (e) { f.focus(); } }
  }

  // ── panel ────────────────────────────────────────────────────────────────
  // El CSS se inyecta apenas corre el script, no cuando se abre el panel: así el botón
  // ya nace con su estilo y no hay un cuadro en que se vea sin formato.
  let cssPuesto = false;
  function inyectarCSS() {
    if (cssPuesto) return; cssPuesto = true;
    const st = document.createElement('style'); st.textContent = CSS; document.head.appendChild(st);
  }
  function construir() {
    if (pan) return;
    inyectarCSS();
    ov = document.createElement('div'); ov.className = 'mt-ov'; ov.onclick = cerrar;
    pan = document.createElement('div'); pan.className = 'mt-p'; conFuente(pan);
    pan.setAttribute('role', 'dialog'); pan.setAttribute('aria-label', 'Encuentra tu talla con MarIA');
    pan.innerHTML =
      '<div class="mt-h">'
      + '<button class="mt-x" aria-label="Cerrar">&times;</button>'
      + '<div class="mt-marca"><b>MACO</b><i></i>'
      + '<span><em>MarIA</em><p>Te ayudo con tu talla</p></span></div>'
      + '</div><div class="mt-b"></div>';
    pan.querySelector('.mt-x').onclick = cerrar;
    cuerpo = pan.querySelector('.mt-b');
    document.body.appendChild(ov); document.body.appendChild(pan);
    document.addEventListener('keydown', e => { if (e.key === 'Escape') cerrar(); focoDentro(e); });
  }
  const abrir = () => {
    if (!pan) return;
    ocultarChat();
    if (btnActual) btnActual.classList.add('mt-oculto');
    bloquearScroll();
    ov.classList.add('on'); pan.classList.add('on');
    evento('abrir');
    paso1();
    enfocarPrimero();
  };
  const cerrar = () => {
    if (!pan || !pan.classList.contains('on')) return;
    // Si se cierra estando en las medidas, el panel vuelve a su ancho normal: la
    // próxima vez tiene que abrirse como siempre.
    tokenMedidas++;
    ov.classList.remove('on'); pan.classList.remove('on'); pan.classList.remove('mt-medidas');
    liberarScroll();
    mostrarChat();
    if (btnActual) {
      btnActual.classList.remove('mt-oculto');
      try { btnActual.focus({ preventScroll: true }); } catch (e) {}
    }
  };
  const pasoTxt = n => '<div class="mt-paso">Paso ' + n + ' de 3</div>';

  // ── paso 1 ───────────────────────────────────────────────────────────────
  function paso1() {
    if (!PROD) return errorUI();
    if (PROD.tallas.length === 1) return tallaUnica();

    const esBottom = PROD.familia === 'bottom';
    const ops = esBottom ? [34, 36, 38, 40, 42] : ['XS', 'S', 'M', 'L', 'XL'];
    const actual = esBottom ? DATOS.numero : DATOS.habitual;
    // Sin titular de relleno: la pregunta es el titular.
    cuerpo.innerHTML = pasoTxt(1)
      + '<h3 class="mt-t mt-pregunta">'
      + (esBottom ? '¿Qué talla de pantalón usas normalmente?' : '¿Qué talla usas normalmente?') + '</h3>'
      + '<div class="mt-ops" style="--mt-cols:' + Math.min(ops.length, 5) + '">' + ops.map(o =>
          '<button class="mt-op' + (String(actual) === String(o) ? ' on' : '') + '" data-v="' + o + '">' + o + '</button>').join('')
      + '</div>'
      + '<button class="mt-min" id="mt-nose">No estoy segura</button>'
      // Segunda ruta, ofrecida de entrada: hay clientas que no vienen a que les
      // recomienden nada, vienen a ver cuánto mide la prenda. No se les cobra el
      // cuestionario por eso.
      + accesoMedidas();
    cuerpo.querySelectorAll('.mt-ops .mt-op').forEach(b => b.onclick = () => {
      const v = b.dataset.v;
      guardar(esBottom ? { numero: +v, habitual: null } : { habitual: v, numero: null });
      paso2();
    });
    cuerpo.querySelector('#mt-nose').onclick = () => { guardar({ numero: null, habitual: null }); paso2(); };
    enganchaAcceso('inicio', paso1, null);
    enfocarPrimero();
  }

  // ── paso 2 ───────────────────────────────────────────────────────────────
  function paso2() {
    const cal = DATOS.calce || 'regular';
    cuerpo.innerHTML = pasoTxt(2)
      + '<h3 class="mt-t">Cuéntame un poquito más</h3>'
      + '<p class="mt-q">Con esto calculo tu talla en esta prenda.</p>'
      + '<div class="mt-f" id="mt-fa"><label>¿Cuánto mides?</label>'
      + '<span class="mt-inp"><input id="mt-a" inputmode="numeric" placeholder="160" value="' + (DATOS.altura || '') + '"><span>cm</span></span>'
      + '<div class="mt-err">Ponlo en centímetros, por ejemplo 160</div></div>'
      + '<div class="mt-f" id="mt-fp"><label>¿Cuánto pesas aproximadamente?</label>'
      + '<span class="mt-inp"><input id="mt-p" inputmode="numeric" placeholder="50" value="' + (DATOS.peso || '') + '"><span>kg</span></span>'
      + '<div class="mt-err">Ponlo en kilos, por ejemplo 50</div></div>'
      + '<div class="mt-f"><label>¿Cómo te gusta que te quede?</label>'
      + '<div class="mt-ops ancho" id="mt-c">'
      + [['ajustado', 'Más ajustada'], ['regular', 'Normal'], ['holgado', 'Más suelta']]
          .map(([v, l]) => '<button class="mt-op' + (cal === v ? ' on' : '') + '" data-v="' + v + '">' + l + '</button>').join('')
      + '</div></div>'
      + '<button class="mt-cta" id="mt-ok">Encontrar mi talla</button>'
      + '<div class="mt-nota">' + (PROD && PROD.fuente_ajuste ? 'Recomendación orientativa según el tallaje Maco. Confirma con la guía de medidas.' : 'Usamos las medidas reales de esta prenda para recomendarte una talla.') + '</div>'
      + '<button class="mt-min" id="mt-volver">Volver</button>';

    let calce = cal;
    cuerpo.querySelectorAll('#mt-c .mt-op').forEach(b => b.onclick = () => {
      cuerpo.querySelectorAll('#mt-c .mt-op').forEach(x => x.classList.remove('on'));
      b.classList.add('on'); calce = b.dataset.v;
    });
    cuerpo.querySelector('#mt-volver').onclick = paso1;
    cuerpo.querySelector('#mt-ok').onclick = () => {
      const a = leer(cuerpo.querySelector('#mt-a').value), p = leer(cuerpo.querySelector('#mt-p').value);
      marcar('#mt-fa', '#mt-a', a.n != null); marcar('#mt-fp', '#mt-p', p.n != null);
      if (a.n == null || p.n == null) return;
      revisar(a, p, calce);
    };
    setTimeout(() => { const i = cuerpo.querySelector('#mt-a'); if (i && !i.value) i.focus(); }, 50);
  }

  // Cada pantalla nueva reengancha el foco: si no, después de avanzar un paso el tabulador
  // se iría a la página de atrás, que está tapada por el modal.
  function marcar(caja, input, ok) {
    cuerpo.querySelector(caja).classList.toggle('mal', !ok);
    cuerpo.querySelector(input).classList.toggle('mal', !ok);
  }

  // Sólo lee el número. No lo corrige ni lo convierte: si escribió 1,60 eso se le
  // pregunta, no se adivina por ella.
  function leer(v) {
    const crudo = String(v || '').trim();
    if (!crudo) return { crudo, n: null };
    const n = parseFloat(crudo.replace(',', '.').replace(/[^\d.]/g, ''));
    if (isNaN(n) || n <= 0) return { crudo, n: null };
    // Entre 1 y 3 puede ser una altura en metros ("1,60") — se pregunta, no se asume.
    return { crudo, n, enMetros: n < 3 };
  }

  const L = LIMITES;
  const fuera = (n, [lo, hi]) => n < lo || n > hi;
  // El límite atípico es inclusivo por arriba: 100 kg exactos ya se confirman, que es
  // justamente el número redondo que alguien escribe cuando se le escapó un dígito.
  const raro = (n, [lo, hi]) => n < lo || n >= hi;

  /**
   * Tres filtros antes de calcular, en este orden:
   *   1. formato  — "1,60" puede ser 160 cm o un error; se pregunta.
   *   2. técnico  — fuera de esto es un error de tipeo seguro; no se avanza.
   *   3. atípico  — puede ser perfectamente real; se confirma y se sigue.
   */
  function revisar(a, p, calce, saltarConfirmacion) {
    if (a.enMetros) return pasoFormato('altura', a.crudo, Math.round(a.n * 100), a, p, calce);
    if (p.enMetros && p.n < 3) return pasoFormato('peso', p.crudo, null, a, p, calce);

    const alt = Math.round(a.n), pes = Math.round(p.n);
    if (fuera(alt, L.tecnico.altura) || fuera(pes, L.tecnico.peso)) return pasoLimite();

    const raros = [];
    if (raro(alt, L.atipico.altura)) raros.push('altura');
    if (raro(pes, L.atipico.peso)) raros.push('peso');
    if (raros.length && !saltarConfirmacion) {
      evento('dato_atipico_detectado', { campos: raros, altura: alt, peso: pes });
      return pasoConfirmar(alt, pes, calce, raros);
    }
    guardar({ altura: alt, peso: pes, calce });
    paso3();
  }

  // ── formato: "¿quisiste decir 160 cm?" ──────────────────────────────────
  function pasoFormato(campo, crudo, sugerido, a, p, calce) {
    const esAltura = campo === 'altura';
    cuerpo.innerHTML = pasoTxt(2)
      + '<h3 class="mt-t">' + (sugerido ? '¿Quisiste decir ' + sugerido + ' cm?' : 'Revisemos ese dato') + '</h3>'
      + '<p class="mt-q">Escribiste <b>' + esc(crudo) + '</b> en ' + (esAltura ? 'la altura' : 'el peso')
      + '. ' + (sugerido ? 'Lo necesito en centímetros 😊' : 'Lo necesito en kilos, por ejemplo 50.') + '</p>'
      + (sugerido ? '<button class="mt-cta" id="mt-si">Sí, ' + sugerido + ' cm</button>' : '')
      + '<button class="mt-min" id="mt-no">Corregir</button>';
    const si = cuerpo.querySelector('#mt-si');
    if (si) si.onclick = () => {
      evento('dato_corregido', { campo, de: crudo, a: sugerido });
      revisar(esAltura ? { crudo: String(sugerido), n: sugerido } : a,
              esAltura ? p : { crudo: String(sugerido), n: sugerido }, calce);
    };
    cuerpo.querySelector('#mt-no').onclick = () => paso2();
  }

  // ── límite técnico: no se puede avanzar ─────────────────────────────────
  function pasoLimite() {
    cuerpo.innerHTML = pasoTxt(2)
      + '<h3 class="mt-t">Revisemos los datos</h3>'
      + '<p class="mt-q">Parece que uno de los datos no está bien escrito. Revísalo para que '
      + 'podamos recomendarte una talla.</p>'
      + '<button class="mt-cta" id="mt-rev">Revisar mis datos</button>';
    cuerpo.querySelector('#mt-rev').onclick = () => paso2();
  }

  // ── dato poco habitual: se confirma, no se corrige ni se rechaza ────────
  function pasoConfirmar(alt, pes, calce, raros) {
    const soloPeso = raros.length === 1 && raros[0] === 'peso';
    const soloAlt  = raros.length === 1 && raros[0] === 'altura';
    let pregunta;
    if (soloPeso && pes < L.atipico.peso[0])
      pregunta = '¿Me confirmas que tu peso es <b>' + pes + ' kg</b>? Puede que se haya escapado un número 😊';
    else if (soloPeso)
      pregunta = 'Antes de seguir, ¿me confirmas que tu peso es <b>' + pes + ' kg</b>?';
    else if (soloAlt)
      pregunta = 'Antes de seguir, ¿me confirmas que mides <b>' + alt + ' cm</b>?';
    else
      pregunta = 'Antes de seguir, ¿me confirmas que estos datos están bien? 😊';

    cuerpo.innerHTML = pasoTxt(2)
      + '<h3 class="mt-t">Un segundo antes de calcular</h3>'
      + '<p class="mt-q">' + pregunta + '</p>'
      + '<div class="mt-conf-datos"><span>Altura<b>' + alt + ' cm</b></span>'
      + '<span>Peso<b>' + pes + ' kg</b></span></div>'
      + '<button class="mt-cta" id="mt-si">Sí, están bien</button>'
      + '<button class="mt-min" id="mt-no">Corregir mis datos</button>';
    cuerpo.querySelector('#mt-si').onclick = () => {
      evento('dato_atipico_confirmado', { campos: raros, altura: alt, peso: pes });
      revisar({ crudo: String(alt), n: alt }, { crudo: String(pes), n: pes }, calce, true);
    };
    cuerpo.querySelector('#mt-no').onclick = () => { evento('dato_corregido', { campos: raros }); paso2(); };
  }

  // ── la medida que zanja la duda ─────────────────────────────────────────
  // Se pregunta sólo cuando la talla que usa y sus medidas no coinciden. Es el único
  // momento en que una pregunta más cambia la respuesta, así que es el único en que
  // se hace. Si prefiere no medirse, mandan la talla que ella declaró.
  const NOMBRE_MEDIDA = { busto: 'contorno de busto', cintura: 'cintura', cadera: 'cadera' };
  const EJEMPLO_MEDIDA = { busto: '90', cintura: '72', cadera: '98' };
  function pasoMedida(medida, seguir) {
    cuerpo.innerHTML = pasoTxt(3)
      + '<h3 class="mt-t">Una última cosa 🫶</h3>'
      + '<p class="mt-q">Para recomendarte bien, ¿cuánto mide aproximadamente tu '
      + NOMBRE_MEDIDA[medida] + '?</p>'
      + '<div class="mt-f" id="mt-fm">'
      + '<span class="mt-inp"><input id="mt-m" inputmode="numeric" placeholder="' + EJEMPLO_MEDIDA[medida] + '"><span>cm</span></span>'
      + '<div class="mt-err">Ponlo en centímetros, por ejemplo ' + EJEMPLO_MEDIDA[medida] + '</div></div>'
      + '<button class="mt-cta" id="mt-ok2">Listo</button>'
      + '<button class="mt-min" id="mt-sin">Prefiero no medirme</button>';
    cuerpo.querySelector('#mt-ok2').onclick = () => {
      const v = leer(cuerpo.querySelector('#mt-m').value);
      const [lo, hi] = LIMITES.medidaValida[medida];
      const ok = v.n != null && v.n >= lo && v.n <= hi;
      marcar('#mt-fm', '#mt-m', ok);
      if (!ok) return;
      const m = Object.assign({}, DATOS.medidas || {}); m[medida] = Math.round(v.n);
      guardar({ medidas: m });
      evento('medida_ingresada', { medida });
      seguir();
    };
    cuerpo.querySelector('#mt-sin').onclick = () => { evento('medida_omitida', { medida }); seguir(true); };
  }

  // ── paso 3 ───────────────────────────────────────────────────────────────
  async function paso3(sinPreguntar) {
    const stock = await stockActual(PROD.handle);
    let r;
    try {
      r = recomendarTalla({
        numeroPantalon: DATOS.numero, tallaHabitual: DATOS.habitual,
        altura: DATOS.altura, peso: DATOS.peso, calce: DATOS.calce || 'regular',
        medidas: DATOS.medidas || null,
        handle: PROD.handle, stock: stock || undefined,
      }, DATASET);
    } catch (e) {
      fallo('calculo', e);
      return errorUI();
    }
    if (!r || !r.ok) fallo('sin_resultado', (r && r.motivo) || 'respuesta vacía');
    ultimo = r;

    // Su talla habitual y sus medidas discrepan por una talla. En vez de elegir por
    // ella, se le pide la medida que zanja la duda. Es la única pregunta que cambia
    // la respuesta, así que es la única que vale la pena hacer.
    if (r.ok && r.ambiguo && !sinPreguntar && !(DATOS.medidas || {})[r.ambiguo.medida]) {
      evento('duda_entre_dos_tallas', { medida: r.ambiguo.medida,
        declarada: r.ambiguo.declarada, por_cuerpo: r.ambiguo.porCuerpo });
      return pasoMedida(r.ambiguo.medida, omitida => paso3(omitida));
    }

    const agotada = r.ok && r.stock_conocido && r.hay_stock === false;
    // "Sin talla" es no poder recomendar con seguridad. No se muestra ninguna talla
    // en grande, porque no hay ninguna que estemos recomendando.
    const sinTalla = !r.ok || !r.talla || r.ajuste === 'fuera_de_rango';

    if (sinTalla) { evento('sin_talla_compatible', { motivo: r.motivo || r.ajuste || null }); return resSinTalla(r); }
    evento('recomendacion', {
      talla: r.talla, segunda_opcion: r.alternativa || null, confianza: r.confianza,
      via: r.via, entre_dos_tallas: !!r.alternativa, medida_real: (r.medidas_reales || []).length > 0,
      altura: DATOS.altura, peso: DATOS.peso, calce: DATOS.calce || 'regular',
      hay_stock: r.stock_conocido ? r.hay_stock : null,
    });
    if (agotada) { evento('talla_recomendada_agotada', { talla: r.talla }); return resAgotada(r); }
    return resNormal(r);
  }

  // ── resultado: hay talla y está disponible ──────────────────────────────
  function resNormal(r) {
    cuerpo.innerHTML = pasoTxt(3)
      + '<div class="mt-r">'
      + '<div class="mt-sello">Tu talla recomendada</div>'
      + '<div class="mt-big">' + esc(r.talla) + '</div>'
      + '<p>' + texto(r) + '</p>'
      + '<button class="mt-cta" id="mt-sel">Elegir talla ' + esc(r.talla) + '</button>'
      + cajaConfianza()
      + '<button class="mt-min" id="mt-otra">Cambiar mis datos</button>'
      + accesoMedidas()
      + '</div>';
    cuerpo.querySelector('#mt-sel').onclick = () => {
      evento('elegir_talla', { talla: r.talla, era: 'recomendada' });
      seleccionarEnFicha(r.talla);
      cerrar();
    };
    enganchaComunes(r, () => resNormal(r));
  }

  // ── resultado: su talla existe pero está agotada ────────────────────────
  // No se la cambiamos por la que sí hay. Se le dice cuál es la suya y que no está.
  function resAgotada(r) {
    const alt = r.sugerencia_stock;
    cuerpo.innerHTML = pasoTxt(3)
      + '<div class="mt-r">'
      + '<div class="mt-sello">No nos queda la talla que necesitas</div>'
      + '<div class="mt-big">' + esc(r.talla) + '</div>'
      + '<div class="mt-agot">Agotada</div>'
      + '<p>Por tus medidas, la talla que te recomendamos es la <strong>' + esc(r.talla)
      + '</strong>. En este momento está agotada y preferimos no sugerirte otra que podría '
      + 'no quedarte como esperas 🫶'
      + (alt ? ' Eso sí: si la prefieres ' + (alt.direccion === 'mayor' ? 'un poquito más suelta' : 'un poquito más al cuerpo')
              + ', la <strong>' + esc(alt.talla) + '</strong> también podría funcionarte.' : '')
      + '</p>'
      + '<button class="mt-cta" id="mt-seguir">Seguir viendo</button>'
      + '<button class="mt-min" id="mt-otra">Cambiar mis datos</button>'
      + accesoMedidas()
      + '</div>';
    cuerpo.querySelector('#mt-seguir').onclick = () => { evento('seguir_viendo', { desde: 'agotada' }); cerrar(); };
    enganchaComunes(r, () => resAgotada(r));
  }

  // ── resultado: no podemos recomendar con seguridad ──────────────────────
  // Sin tallas, sin otros productos y sin cantidades: no hemos comparado sus medidas
  // con las de esas otras prendas, así que prometer que le quedarán sería inventar.
  function resSinTalla(r) {
    cuerpo.innerHTML = pasoTxt(3)
      + '<div class="mt-r">'
      + '<div class="mt-sello">No tenemos una talla para recomendarte</div>'
      + '<p class="mt-sep">Con las medidas que nos comentaste, no tenemos una talla de esta prenda '
      + 'que podamos recomendarte con seguridad. Preferimos decírtelo antes que sugerirte una que '
      + 'probablemente no te quede como esperas 🫶</p>'
      + '<button class="mt-cta" id="mt-otra2">Revisar mis datos</button>'
      + accesoMedidas()
      + '<button class="mt-min" id="mt-seguir">Seguir viendo</button>'
      + '</div>';
    cuerpo.querySelector('#mt-otra2').onclick = () => { evento('cambio_datos'); paso2(); };
    cuerpo.querySelector('#mt-seguir').onclick = () => { evento('seguir_viendo', { desde: 'sin_talla' }); cerrar(); };
    enganchaAcceso('sin_talla', () => resSinTalla(r), null);
  }

  function enganchaComunes(r, volver) {
    const o = cuerpo.querySelector('#mt-otra');
    if (o) o.onclick = () => { evento('cambio_datos'); paso2(); };
    // Al volver de las medidas se vuelve a ESTA pantalla, no al principio: la clienta
    // fue a mirar un dato, no a empezar de nuevo.
    enganchaAcceso('resultado', volver, r && r.talla);
    enganchaFila(cuerpo);
  }

  // Antes, acá adentro, había un desplegable con una tabla de cuatro columnas —
  // cintura, cadera, busto y ancho— y nada más: se perdían el largo, las mangas, la
  // espalda y las equivalencias de numeración que la guía sí trae. Ahora el mismo
  // botón lleva a la vista completa, que es la misma que se ofrece en la primera
  // pantalla. Una sola forma de mostrar las medidas en todo el asistente.

  // ══ Medidas de esta prenda ══════════════════════════════════════════════
  //
  // Segunda ruta del asistente: no pregunta nada y muestra lo que la guía de la
  // tienda dice de ESTA prenda. Se entra desde la primera pantalla y desde el
  // resultado, y se sale con "Volver al recomendador" — siempre dentro del mismo
  // panel, sin abrir una página ni una segunda ventana.
  //
  // De dónde salen los datos, en este orden:
  //   1. La tabla original de la guía. El tema ya la trae dentro de la ficha (en el
  //      modal que abre el enlace "Guía de Tallas"), y el widget la esconde. Es la
  //      fuente completa: trae columnas que el dataset no guarda —largo de cuerpo,
  //      tajo— y los rangos tal como están escritos ("78-90 cm"), no su promedio.
  //   2. Si esa tabla no está en el DOM, se pide la página de la guía al propio
  //      sitio, mismo origen, y se lee de ahí.
  //   3. Si tampoco, el dataset. Se muestra lo que hay y nada más.
  //
  // Regla de oro: nunca se completa, convierte ni redondea una celda. Lo que no
  // está en la guía no se muestra.

  const ETIQUETA_MEDIDA = {
    busto: 'Contorno busto', cintura: 'Contorno cintura', cadera: 'Contorno cadera',
    ancho: 'Ancho', espalda: 'Espalda', largo: 'Largo', largo_manga: 'Largo de manga',
  };
  const ORDEN_MEDIDA = ['busto', 'cintura', 'cadera', 'ancho', 'espalda', 'largo', 'largo_manga'];

  const sinTildes = s => String(s == null ? '' : s).normalize('NFD').replace(/[̀-ͯ]/g, '');
  const clave = s => sinTildes(s).toLowerCase().replace(/\s+/g, ' ').trim();
  const esColumnaTalla = s => /^(talla|tamano|size)\b/.test(clave(s));

  /**
   * Qué medida del dataset representa una columna de la guía. Sólo sirve para saber si
   * una columna ya está cubierta; el texto que se muestra siempre es el de la guía.
   */
  function medidaDe(etiqueta) {
    const t = clave(etiqueta);
    if (!t) return null;
    if (/manga/.test(t)) return 'largo_manga';
    if (/espalda/.test(t)) return 'espalda';
    if (/busto|pecho/.test(t)) return 'busto';
    if (/cintura/.test(t)) return 'cintura';
    if (/cadera/.test(t)) return 'cadera';
    if (/^ancho\b/.test(t)) return 'ancho';
    if (/^largo\b/.test(t)) return 'largo';
    return null;
  }

  /** Una celda vale si tiene algún número. Se muestra tal como está escrita. */
  function celdaGuia(txt) {
    const v = String(txt == null ? '' : txt).replace(/\s+/g, ' ').trim();
    return v && /\d/.test(v) ? v : null;
  }

  const talladoCanon = t => {
    const s = sinTildes(t).toUpperCase().replace(/\s+/g, ' ').trim();
    return /^(TALLA )?(UNICA|U|TU)$/.test(s) ? 'TU' : s;
  };

  /** Lee las tablas de medidas que haya bajo una raíz. Descarta lo que no sea de tallas. */
  function tablasBajo(raiz) {
    if (!raiz || !raiz.querySelectorAll) return [];
    const ts = [];
    const nodos = raiz.querySelectorAll('table');
    for (let i = 0; i < nodos.length; i++) {
      const filas = [];
      const rs = nodos[i].rows;
      for (let j = 0; j < rs.length; j++) {
        const cs = rs[j].cells, fila = [];
        for (let k = 0; k < cs.length; k++) fila.push((cs[k].textContent || '').replace(/\s+/g, ' ').trim());
        filas.push(fila);
      }
      if (filas.length >= 2 && esColumnaTalla(filas[0][0])) ts.push(filas);
    }
    return ts;
  }

  /**
   * Busca la guía partiendo del propio enlace y subiendo. El primer nivel que contenga
   * una tabla de tallas gana: así no se agarra una tabla de otra parte de la página.
   */
  function tablasGuia() {
    const zonas = [];
    let n = anclaGuiaNodo;
    for (let i = 0; i < 6 && n; i++) { zonas.push(n); n = n.parentElement; }
    zonas.push(ambito(), document);
    for (let i = 0; i < zonas.length; i++) {
      const ts = tablasBajo(zonas[i]);
      if (ts.length) return ts;
    }
    return [];
  }

  // Si la guía no está en la ficha, se pide su página al propio sitio. Una sola vez.
  let guiaRemota = null;
  function pedirGuia() {
    if (guiaRemota) return guiaRemota;
    const href = anclaGuiaNodo && anclaGuiaNodo.getAttribute && anclaGuiaNodo.getAttribute('href');
    // sólo rutas del propio sitio: nada de dominios de fuera ni de "//otro.com"
    if (!href || !/^\/[^/]/.test(href)) return (guiaRemota = Promise.resolve([]));
    guiaRemota = fetch(href, { credentials: 'same-origin' })
      .then(r => (r.ok ? r.text() : null))
      .then(h => (h ? tablasBajo(new DOMParser().parseFromString(h, 'text/html')) : []))
      .catch(() => []);
    return guiaRemota;
  }

  /** Convierte las tablas crudas de la guía en bloques listos para pintar. */
  function bloquesDeGuia(tablas) {
    const out = [];
    for (let i = 0; i < (tablas || []).length; i++) {
      const cab = tablas[i][0];
      const cuerpoF = tablas[i].slice(1).filter(r => r.some(c => c));
      if (!cuerpoF.length) continue;
      const cols = [];
      for (let c = 1; c < cab.length; c++) {
        const bruta = (cab[c] || '').replace(/\s+/g, ' ').trim();
        const vals = cuerpoF.map(r => celdaGuia(r[c]));
        // columna sin título, o sin un solo dato: no se muestra
        if (!bruta || !vals.some(v => v)) continue;
        // La guía llama "Talla" a la equivalencia europea, igual que a la talla de la
        // fila. Se distingue con la palabra que la propia guía usa más abajo.
        cols.push({ etiqueta: esColumnaTalla(bruta) ? bruta + ' (equivalencia)' : bruta, valores: vals });
      }
      if (!cols.length) continue;
      out.push({
        columnas: cols.map(c => c.etiqueta),
        // Qué medida del motor representa cada columna. No se usa para pintar —para eso
        // manda el texto de la guía— pero queda disponible en MacoTallas.medidas() para
        // que MarIA pueda responder "¿cuánto mide el busto?" sin volver a interpretar nada.
        medidas: cols.map(c => medidaDe(c.etiqueta)),
        filas: cuerpoF.map((r, j) => ({
          talla: (r[0] || '').replace(/\s+/g, ' ').trim(),
          valores: cols.map(c => c.valores[j]),
        })),
      });
    }
    return out;
  }

  /**
   * La guía puede escribir las tallas distinto a como se venden ("Única S" por TU). Se
   * alinean sólo cuando calzan por nombre; si no calza ninguna, se dejan las etiquetas de
   * la guía tal cual. Adivinar por posición sería inventar a qué talla corresponde cada fila.
   */
  function conTallasDeLaTienda(bloque) {
    const vende = (PROD && PROD.tallas) || [];
    if (!vende.length) return bloque;
    const filas = bloque.filas.filter(f => vende.indexOf(talladoCanon(f.talla)) >= 0);
    if (!filas.length) return bloque;
    return {
      columnas: bloque.columnas, medidas: bloque.medidas,
      // Se escribe la talla como la escribe el recomendador en el resto del asistente
      // (XS, SM, TU…), no como la escribió quien redactó la guía: dentro del mismo panel
      // la misma talla tiene que llamarse siempre igual.
      filas: filas.map(f => {
        const c = talladoCanon(f.talla);
        return { talla: c, valores: f.valores, canon: c };
      }),
    };
  }

  /**
   * El dataset calcula el busto de algunas prendas como el ancho por dos, para poder
   * compararlas con el resto del catálogo. Eso sirve para elegir una talla, pero no es
   * un número que la guía diga: mostrarlo sería inventar una medida.
   */
  function bustoDerivado(fila) {
    return fila.busto != null && fila.ancho != null && fila.ancho < 80
      && Math.abs(fila.busto - Math.round(fila.ancho * 2 * 10) / 10) < 0.05;
  }

  /** Bloques a partir del dataset, para cuando la guía original no está disponible. */
  function bloquesDeDataset() {
    const filas = (PROD && (PROD.medidas_reales || PROD.medidas)) || [];
    if (!filas.length) return [];
    // Una talla repetida quiere decir que la guía traía más de una tabla (prendas de dos
    // piezas). Se corta ahí, respetando el orden en que vino.
    const grupos = []; let actual = [], vistas = {};
    for (const f of filas) {
      if (vistas[f.talla]) { grupos.push(actual); actual = []; vistas = {}; }
      vistas[f.talla] = 1; actual.push(f);
    }
    if (actual.length) grupos.push(actual);

    const vende = (PROD && PROD.tallas) || [];
    const out = [];
    for (const g of grupos) {
      const usar = vende.length && g.some(f => vende.indexOf(f.talla) >= 0)
        ? g.filter(f => vende.indexOf(f.talla) >= 0) : g;
      const claves = ORDEN_MEDIDA.filter(k =>
        usar.some(f => f[k] != null && !(k === 'busto' && bustoDerivado(f))));
      const otros = [];
      for (const f of usar) for (const k of Object.keys(f.otros || {}))
        if (otros.indexOf(k) < 0) otros.push(k);
      if (!claves.length && !otros.length) continue;
      out.push({
        columnas: claves.map(k => ETIQUETA_MEDIDA[k])
          .concat(otros.map(k => (esColumnaTalla(k) ? k + ' (equivalencia)' : k))),
        medidas: claves.concat(otros.map(() => null)),
        filas: usar.map(f => ({
          talla: f.talla, canon: f.talla,
          valores: claves.map(k => (f[k] != null && !(k === 'busto' && bustoDerivado(f)) ? f[k] + ' cm' : null))
            .concat(otros.map(k => (f.otros && f.otros[k] != null ? String(f.otros[k]) : null))),
        })),
      });
    }
    return out;
  }

  let medidasCache = null;
  /** Lo mejor que tengamos ahora mismo, sin esperar a nada. */
  function medidasDisponibles() {
    if (medidasCache) return medidasCache;
    const g = bloquesDeGuia(tablasGuia()).map(conTallasDeLaTienda).filter(b => b.filas.length);
    if (g.length) return (medidasCache = { bloques: g, fuente: 'guia' });
    return { bloques: bloquesDeDataset(), fuente: 'dataset' };
  }
  const hayMedidas = () => medidasDisponibles().bloques.length > 0;

  /** Botón de entrada a la vista. Se omite si no hay nada que mostrar. */
  function accesoMedidas() {
    if (!hayMedidas()) return '';
    return '<div class="mt-md-otra"><button type="button" class="mt-cta2" id="mt-ver-medidas">'
      + ICONO_REGLA + '<span>Ver medidas de esta prenda</span></button></div>';
  }
  function enganchaAcceso(donde, volver, destacar) {
    const b = cuerpo.querySelector('#mt-ver-medidas');
    if (b) b.onclick = () => verMedidas(donde, volver, destacar);
    // Si la guía no estaba en la ficha hay que ir a buscarla; se hace al pasar por
    // encima del botón, no al cargar la página, para no pedir nada que nadie va a usar.
    if (b && medidasDisponibles().fuente !== 'guia') {
      const pre = () => pedirGuia();
      b.addEventListener('mouseenter', pre, { once: true });
      b.addEventListener('focus', pre, { once: true });
      b.addEventListener('touchstart', pre, { once: true, passive: true });
    }
  }

  let tokenMedidas = 0;
  function verMedidas(donde, volver, destacar) {
    const datos = medidasDisponibles();
    evento('apertura_medidas', {
      donde: donde, fuente: datos.fuente, tablas: datos.bloques.length,
      columnas: datos.bloques.reduce((n, b) => n + b.columnas.length, 0),
      talla_destacada: destacar || null,
    });
    const token = ++tokenMedidas;
    pintarMedidas(datos, volver, destacar, true);
    // Si lo que se pintó no venía de la guía, se intenta traerla y se repinta sin mover
    // el foco. Si no llega, se queda lo que ya está: nunca se deja la pantalla vacía.
    if (datos.fuente !== 'guia') pedirGuia().then(ts => {
      if (token !== tokenMedidas) return;
      const g = bloquesDeGuia(ts).map(conTallasDeLaTienda).filter(b => b.filas.length);
      if (!g.length) return;
      medidasCache = { bloques: g, fuente: 'guia' };
      pintarMedidas(medidasCache, volver, destacar, false);
    });
  }

  function pintarMedidas(datos, volver, destacar, enfocar) {
    const varias = datos.bloques.length > 1;
    const titulo = (PROD && PROD.titulo) || 'esta prenda';
    let html = '<button type="button" class="mt-volver" id="mt-md-volver">'
      + ICONO_ATRAS + '<span>Volver al recomendador</span></button>'
      + '<div class="mt-md-cab"><h3 class="mt-md-t1">' + esc(titulo) + '</h3>'
      + '<p class="mt-md-t2">Guía de tallas en cm</p>'
      + (varias ? '<p class="mt-md-nota">La guía de esta prenda trae ' + datos.bloques.length
          + ' tablas de medidas.</p>' : '')
      + '</div>';

    if (!datos.bloques.length) {
      html += '<div class="mt-md-sin">Todavía no tenemos cargadas las medidas de esta prenda. '
        + 'Si quieres, te ayudo a encontrar tu talla con tus datos 🫶</div>';
    }

    for (let n = 0; n < datos.bloques.length; n++) {
      const b = datos.bloques[n];
      html += '<div class="mt-md-bloque">'
        + (varias ? '<p class="mt-md-bt">Tabla ' + (n + 1) + '</p>' : '')
        + '<table class="mt-md-t"><caption>Medidas de ' + esc(titulo) + ' en centímetros'
        + (varias ? ', tabla ' + (n + 1) + ' de ' + datos.bloques.length : '') + '</caption>'
        + '<thead><tr><th scope="col">Talla</th>'
        + b.columnas.map(c => '<th scope="col">' + esc(c) + '</th>').join('')
        + '</tr></thead><tbody>'
        + b.filas.map(f => {
            const suya = destacar && (f.canon || f.talla) === destacar;
            return '<tr' + (suya ? ' class="on"' : '') + '>'
              + '<th scope="row">' + esc(f.talla)
              + (suya ? '<span class="mt-md-tag">Tu talla</span>' : '') + '</th>'
              + f.valores.map((v, i) => '<td data-e="' + esc(b.columnas[i]) + '"'
                  + (v ? '' : ' class="sin"') + '>'
                  + (v ? esc(v) : '<span class="mt-md-vacio">—</span>') + '</td>').join('')
              + '</tr>';
          }).join('')
        + '</tbody></table></div>';
    }

    if (PROD && PROD.como_medir)
      html += '<div class="mt-md-medir"><b>Cómo medir</b>' + esc(PROD.como_medir) + '</div>';

    html += '<div class="mt-md-pie"><button type="button" class="mt-cta2" id="mt-md-volver2">'
      + 'Volver al recomendador</button></div>';

    cuerpo.innerHTML = html;
    pan.classList.add('mt-medidas');
    cuerpo.scrollTop = 0;
    const salir = () => { tokenMedidas++; pan.classList.remove('mt-medidas'); volver(); };
    cuerpo.querySelector('#mt-md-volver').onclick = salir;
    cuerpo.querySelector('#mt-md-volver2').onclick = salir;
    if (enfocar) enfocarPrimero();
  }

  // ── textos, en voz de MarIA ─────────────────────────────────────────────
  const ORDEN_T = ['XS', 'S', 'SM', 'M', 'ML', 'L', 'XL'];
  function texto(r) {
    const t = x => '<strong>' + esc(x) + '</strong>';
    const corre = r.producto ? r.producto.calce_relativo : (PROD && PROD.calce_relativo);

    if (r.tallaUnica) {
      return r.confianza === 'alta'
        ? 'Esta viene en talla única y cae suelta, así que te va a quedar bien 🥰'
        : 'Es talla única y por tus medidas te puede quedar justa. Si te gusta así, va; y si no, la cambias sin problema.';
    }

    // La duda entre su talla habitual y sus medidas: se le dijo y ella decidió no medirse.
    if (r.ambiguo)
      return 'Me quedo con la ' + t(r.talla) + ', que es la que usas normalmente 🥰 Por tus medidas '
        + 'podría irse a la ' + esc(r.ambiguo.porCuerpo) + ', así que quedas justo entre las dos. '
        + 'Si la prefieres más suelta, anda por la ' + esc(r.ambiguo.porCuerpo) + '.' + notaLargo(r);

    // La talla que declaró y la que sale de sus medidas no coinciden
    if (r.declarada && r.declarada !== r.talla)
      return 'Por la talla que usas normalmente te recomendaría la ' + t(r.declarada)
        + ', pero por tus medidas y el calce de esta pieza, me iría por la ' + t(r.talla)
        + ' para que te quede más cómoda.' + notaLargo(r);

    // Le queda justa
    if (r.ajuste === 'justa' || /talla mayor/.test(r.nota || ''))
      return 'Te recomiendo la ' + t(r.talla) + ' 🥰 Te va a quedar justa, más al cuerpo que suelta. '
        + 'Si te gusta así, va perfecto. Y si al probártela no te convence, la cambias o la devuelves.';

    // Esta pieza calza distinto al resto de la marca. Ojo: sólo se puede decir "te
    // recomiendo una talla más" cuando de verdad subimos una — si no, el texto
    // describe algo que no hicimos y se lee como un error.
    if (corre === 'corre_chico') {
      const subimos = r.declarada && ORDEN_T.indexOf(r.declarada) >= 0
        && ORDEN_T.indexOf(r.declarada) < ORDEN_T.indexOf(r.talla);
      return 'Te recomiendo la ' + t(r.talla) + ' 🥰 '
        + (subimos ? 'Como esta pieza calza más al cuerpo que el resto, prefiero subirte una talla para que quedes cómoda.'
                   : 'Esta pieza calza un poco más al cuerpo que el resto, y en esta talla te va a quedar bien.')
        + notaLargo(r);
    }
    if (corre === 'corre_grande')
      return 'Te recomiendo la ' + t(r.talla) + ' 🥰 Como esta pieza cae suelta, te va a quedar cómoda.' + notaLargo(r);

    // Está entre dos tallas
    if (r.alternativa) {
      const O = ORDEN_T;
      const a = O.indexOf(r.talla) < O.indexOf(r.alternativa) ? r.talla : r.alternativa;
      const b = a === r.talla ? r.alternativa : r.talla;
      return 'Te recomiendo la ' + t(r.talla) + ' 🥰 Estás entre la ' + esc(a) + ' y la ' + esc(b)
        + '. Si te gusta más al cuerpo, la ' + esc(a) + '; si la prefieres más cómoda, la ' + esc(b) + '.'
        + notaLargo(r);
    }

    // Declaró su talla y coincide con sus medidas
    if (r.declarada || DATOS.numero || DATOS.habitual)
      return 'Te recomiendo la ' + t(r.talla) + ' 🥰 Por la talla que usas normalmente y tus medidas, '
        + 'esta sería la mejor opción para ti.' + notaLargo(r);

    return 'Te recomiendo la ' + t(r.talla) + ' 🥰' + notaLargo(r);
  }

  const notaLargo = r => !r.largo ? ''
    : (r.largo === 'alta' ? ' El largo está pensado para 1,70, así que a ti te va a quedar un poco más corto.'
                          : ' El largo está pensado para 1,70, así que a ti te va a quedar un poco más largo.');

  // Acá vivía una lista de "otras prendas que sí te quedan". Se eliminó a propósito:
  // se construía comparando sólo altura y peso, así que prometía un calce que no
  // habíamos verificado — y además dejaba ver cuántas piezas tenemos. Si algún día se
  // comparan las medidas reales de esas prendas, el texto sería "estas prendas podrían
  // funcionarte mejor", nunca "sí te van a quedar".

  // ── talla única y error ─────────────────────────────────────────────────
  function tallaUnica() {
    const f = (PROD.medidas && PROD.medidas[0]) || {};
    const med = f.ancho ? 'Mide ' + f.ancho + ' cm de ancho' + (f.largo ? ' y ' + f.largo + ' cm de largo' : '') + '.' : '';
    cuerpo.innerHTML = '<div class="mt-r">'
      + '<div class="mt-sello">Talla única</div><div class="mt-big">TU</div>'
      + '<p>Esta viene en talla única y cae suelta, así que te va a quedar bien 🥰 ' + esc(med) + '</p>'
      + '<button class="mt-cta" id="mt-sel">Elegir talla única</button>'
      + cajaConfianza()
      + accesoMedidas()
      + '</div>';
    cuerpo.querySelector('#mt-sel').onclick = () => { evento('elegir_talla', { talla: PROD.tallas[0] }); seleccionarEnFicha(PROD.tallas[0]); cerrar(); };
    enganchaAcceso('talla_unica', tallaUnica, PROD.tallas[0]);
    enganchaFila(cuerpo);
  }
  function errorUI() {
    cuerpo.innerHTML = '<div class="mt-r"><p style="text-align:center">'
      + 'Ups! No pudimos calcular tu talla con esos datos. Revísalos y lo intentamos de nuevo 🫶</p>'
      + (PROD ? '<button class="mt-cta" id="mt-otra">Revisar mis datos</button>' : '')
      + '</div>';
    const o = cuerpo.querySelector('#mt-otra'); if (o) o.onclick = paso2;
  }

  // ── puente a MarIA, con todo precargado ─────────────────────────────────
  function contextoChat(r) {
    return {
      handle: PROD ? PROD.handle : null,
      producto: PROD ? PROD.titulo : null,
      tallas_disponibles: r && r.tabla ? r.tabla.map(f => f.talla) : (PROD ? PROD.tallas : []),
      datos: { altura: DATOS.altura, peso: DATOS.peso, calce: DATOS.calce,
               talla_habitual: DATOS.habitual, numero_pantalon: DATOS.numero },
      talla_recomendada: r ? r.talla : null,
      segunda_opcion: r ? (r.alternativa || null) : null,
      entre_dos_tallas: !!(r && r.alternativa),
      fuera_de_rango: !!(r && r.ajuste === 'fuera_de_rango'),
      talla_agotada: !!(r && r.stock_conocido && r.hay_stock === false),
      talla_declarada: DATOS.habitual || DATOS.numero || null,
      politica: politica(),
    };
  }

  // ── arranque ─────────────────────────────────────────────────────────────
  /**
   * El tema esconde el enlace viejo desde el primer pintado con la clase `mt-cargando`.
   * Pase lo que pase acá dentro —producto fuera del dataset, dataset caído, error— hay que
   * soltar esa clase, o el enlace antiguo quedaría escondido para siempre.
   */
  async function iniciar() {
    try { await montar(); }
    catch (e) { fallo('montaje', e); }
    finally {
      window.MACO_TALLAS_LISTO = true;
      try { document.documentElement.classList.remove('mt-cargando'); } catch (e) {}
    }
  }
  async function montar() {
    if (montado) return; montado = true;
    const handle = handleActual();
    if (!handle) return;
    inyectarCSS();

    // El link "Guía de Tallas" se esconde ANTES de ir a buscar el dataset. Si se hiciera
    // después, la clienta alcanzaría a verlo unos cuadros y luego desaparecería solo.
    // Si el producto no está en el dataset se devuelve tal cual y no se pierde nada.
    // La clase de pre-pintado se suelta justo acá: para localizar el enlace hay que poder
    // verlo. Como esconder() ocurre en el mismo bloque síncrono, el navegador no alcanza a
    // pintar nada entremedio y la clienta nunca ve el enlace viejo.
    document.documentElement.classList.remove('mt-cargando');
    const guia = CFG.reemplazarGuia ? anclaGuia() : null;
    if (guia) esconder(guia);

    if (CFG.dataset) DATASET = CFG.dataset;
    else if (CFG.datos) { try { DATASET = await (await fetch(CFG.datos)).json(); } catch (e) { restaurar(); return; } }
    else { restaurar(); return; }
    PROD = DATASET.productos.find(p => p.handle === handle);
    if (!PROD) { restaurar(); return; }

    montarPolitica();
    // En talla única no hay nada que decidir, así que el botón sería ruido.
    // La caja "Compra tranquila" sí se queda: sirve igual.
    // En talla única no hay nada que decidir: se devuelve el link de la guía y listo.
    if (!PROD.tallas || PROD.tallas.length <= 1) { if (guia) guia.style.display = ocultos.find(o => o[0] === guia)[1]; return; }

    construir();
    const btn = document.createElement('button');
    btn.type = 'button'; btn.className = 'mt-btn'; btn.textContent = CFG.textoBoton; conFuente(btn);
    btn.setAttribute('aria-haspopup', 'dialog');
    btn.onclick = abrir; btnActual = btn;
    const destino = document.querySelector(CFG.montarEn);
    if (destino) { destino.appendChild(btn); destapar(btn); return; }
    // Sin div propio: ocupa el lugar del link "Guía de Tallas", que ya está escondido.
    // El widget trae la misma tabla y el "cómo medir", así que no se pierde nada.
    if (guia) { guia.parentNode.insertBefore(btn, guia); destapar(btn); return; }
    const ref = document.querySelector('variant-radios, variant-selects, .product-form__input, form[action*="/cart/add"]');
    if (ref) { ref.parentNode.insertBefore(btn, ref.nextSibling); destapar(btn); }
  }
  function remontar() {
    if (btnActual && btnActual.parentNode) btnActual.parentNode.removeChild(btnActual);
    if (cajaAuto && cajaAuto.parentNode) cajaAuto.parentNode.removeChild(cajaAuto);
    restaurar();
    btnActual = null; cajaAuto = null; PROD = null; montado = false;
    // Las medidas son de un producto concreto: si se remonta, hay que volver a leerlas.
    // Dejarlas cacheadas mostraría la guía de la prenda anterior.
    medidasCache = null; guiaRemota = null; anclaGuiaNodo = null; tokenMedidas++;
    const d = document.querySelector(CFG.politicaEn); if (d) d.innerHTML = '';
    cerrar(); iniciar();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', iniciar);
  else iniciar();

  // El chatbot ya vive en la página, así que el widget no lo abre. Pero deja listo el
  // contexto por si algún día se quiere pasar sin que la clienta repita nada.
  // `medidas` queda expuesto a propósito: es lo que consultan las pruebas, y el día que
  // MarIA quiera responder "¿cuánto mide el largo?" sale de acá y no de otra copia.
  window.MacoTallas = { abrir, cerrar, iniciar, remontar, politicaHTML, politicaTexto,
                        medidas: () => medidasDisponibles(),
                        contextoParaChat: () => contextoChat(ultimo) };
})();